import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ApiService } from '../services/api';
import { Ticket, Hire, Driver, Vehicle, TimelineEvent } from '../types';
import { ClaudeService } from '../services/claudeService';
import { CouncilPaymentService } from '../services/councilPayments';
import { RecommendationResult } from '../services/recommendationService';
import { RecoveryDecisionService } from '../services/recoveryDecisionService';
import { EmailService } from '../services/emailService';
import { analyzeLiability } from '../services/liabilityEngine';
import { getErrorMessage, isMockMode } from '../services/supabaseClient';
import { useBranding } from '../context/BrandingContext';
import { useAuth } from '../App';
import { useRealtimeRecommendations } from '../hooks/useRealtimeRecommendations';
import {
  ArrowLeft, Clock, MapPin, CreditCard, FileText, Send,
  User, CheckCircle2, AlertOctagon, Download, Sparkles, Loader2,
  FileCheck, Shield, Briefcase, Gavel, Scale, ImageIcon, ExternalLink,
  Copy, Globe, Lock, Car, Calendar, Search, Check, UserCheck, ArrowRight, AlertTriangle, XCircle, Upload,
  Flag, ShieldCheck, Clipboard, Trash2, Receipt
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useSubscription } from '../context/SubscriptionContext';
import Modal from '../components/Modal';
import InvestigationModal from '../components/InvestigationModal';
import CreateInvoiceModal from '../components/CreateInvoiceModal';
import AppealStrategyCard from '../components/AppealStrategyCard';
import AppealChecklistModal from '../components/AppealChecklistModal';
import { isPcnOrNtoStage, normalizeStage } from '../utils/stage';
import { inferEnforcementType, type EnforcementType } from '../utils/enforcement';
import { LIABILITY_CLAUSE_TEMPLATE } from '../utils/liabilityClause';
import { BusinessModelHelpers } from '../utils/businessModel';
import { getCouncilRequirements, isFieldRequired } from '../data/councilFormRequirements';
import { findAuthorityByText } from '../data/issuingAuthorities';
import { generateRepresentation, getTemplateDescription, type TemplateVariant } from '../services/representationTemplates';
import { formatTicketStatus, normalizeTicketStatus } from '../utils/ticketStatus';
import { getTecFormRecommendation } from '../utils/tecForms';
import { fillTecPdf, downloadBytes, TecFormPrefillValues } from '../utils/tecPdf';
import TeFormWizard from '../components/TeFormWizard';
import SubmissionWizard from '../components/SubmissionWizard';
import SubmissionsTimeline from '../components/SubmissionsTimeline';
import { SelectableOptionButton } from '../components/submissionFlowPrimitives';
import { getTicketSubmissions, detectAuthority, createSubmission, uploadProofScreenshot } from '../services/rpaService';
import type { Submission } from '../services/rpaService';
import {
  DriverNotificationService,
  type DriverTicketRecipient
} from '../services/driverNotificationService';

// --- Visual Components ---

const RecommendationBanner: React.FC<{
  result: RecommendationResult | null,
  onApply: (action: string) => void,
  liabilityInfo?: { party: string; confidence: number; tier: number } | null
}> = ({ result, onApply, liabilityInfo }) => {
  if (!result) return null;

  const styles = {
    transfer: { bg: 'bg-indigo-50 dark:bg-indigo-900/20', border: 'border-indigo-200 dark:border-indigo-800', icon: 'text-indigo-600 dark:text-indigo-400', text: 'text-indigo-900 dark:text-indigo-100', btn: 'bg-indigo-600 hover:bg-indigo-700 text-white' },
    pay: { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', icon: 'text-amber-600 dark:text-amber-400', text: 'text-amber-900 dark:text-amber-100', btn: 'bg-amber-600 hover:bg-amber-700 text-white' },
    appeal: { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', icon: 'text-purple-600 dark:text-purple-400', text: 'text-purple-900 dark:text-purple-100', btn: 'bg-purple-600 hover:bg-purple-700 text-white' },
    investigate: { bg: 'bg-slate-50 dark:bg-slate-800/50', border: 'border-slate-200 dark:border-slate-700', icon: 'text-slate-600 dark:text-slate-400', text: 'text-slate-900 dark:text-slate-100', btn: 'bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 text-white' },
  };

  const style = styles[result.action] || styles.investigate;

  return (
    <div className={`w-full p-4 rounded-xl border ${style.bg} ${style.border} mb-6 flex items-start gap-4 shadow-sm animate-in slide-in-from-top-2`}>
      <div className={`p-2 bg-white dark:bg-slate-950 rounded-full shadow-sm ${style.icon}`}>
        <Sparkles size={20} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className={`font-bold ${style.text}`}>FleetMind Recommendation: {result.action.charAt(0).toUpperCase() + result.action.slice(1)}</h3>
          {result.urgent && (
            <span className="text-[10px] font-bold uppercase bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 px-2 py-0.5 rounded border border-red-200 dark:border-red-800">Urgent</span>
          )}
          <span className="text-xs text-slate-700 dark:text-slate-300 font-medium border border-slate-200 dark:border-slate-700 px-2 py-0.5 rounded bg-white dark:bg-slate-900">
            {result.confidence} Confidence
          </span>
          {liabilityInfo && liabilityInfo.party && liabilityInfo.confidence !== undefined && liabilityInfo.tier && (
            <>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded border cursor-help ${
                  liabilityInfo.party === 'fleet' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800' :
                  liabilityInfo.party === 'hire_company' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800' :
                  liabilityInfo.party === 'driver' || liabilityInfo.party === 'driver_internal' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800' :
                  'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                }`}
                title={`Legal Liability: ${
                  liabilityInfo.party === 'fleet' ? 'Fleet is legally liable for this PCN' :
                  liabilityInfo.party === 'hire_company' ? 'Hire company is legally liable - transfer recommended' :
                  liabilityInfo.party === 'driver' ? 'Driver is legally liable - transfer recommended' :
                  liabilityInfo.party === 'driver_internal' ? 'Internal driver is liable - handle internally' :
                  'Liability determination pending'
                } (${Math.round(liabilityInfo.confidence * 100)}% confidence based on hire records and timing)`}
              >
                {liabilityInfo.party === 'hire_company' ? 'Hirer' :
                 liabilityInfo.party === 'driver_internal' ? 'Driver (Internal)' :
                 liabilityInfo.party.charAt(0).toUpperCase() + liabilityInfo.party.slice(1).replace('_', ' ')} ({Math.round(liabilityInfo.confidence * 100)}%)
              </span>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded border cursor-help ${
                  liabilityInfo.tier === 1 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800' :
                  liabilityInfo.tier === 2 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800' :
                  liabilityInfo.tier === 3 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800' :
                  liabilityInfo.tier === 4 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800' :
                  'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800'
                }`}
                title={`Evidence Quality: ${
                  liabilityInfo.tier === 1 ? 'Tier 1 - Strongest: Signed hire agreement with liability clause (can auto-transfer)' :
                  liabilityInfo.tier === 2 ? 'Tier 2 - Strong: Verified hire match with timing evidence (can auto-transfer)' :
                  liabilityInfo.tier === 3 ? 'Tier 3 - Moderate: Hire found but missing documentation (review required)' :
                  liabilityInfo.tier === 4 ? 'Tier 4 - Weak: Limited evidence, driver assigned without agreement (manual review)' :
                  'Tier 5 - None: No hire records found, insufficient data (cannot transfer)'
                }`}
              >
                Tier {liabilityInfo.tier}
              </span>
            </>
          )}
        </div>
        <p className={`text-sm ${style.text} opacity-90 mb-3`}>{result.reason}</p>
        <button
          onClick={() => onApply(result.action)}
          className={`text-xs font-bold px-4 py-2 rounded-lg transition-colors ${style.btn} flex items-center gap-2 shadow-sm`}
        >
          {result.action === 'transfer' && <Briefcase size={14} />}
          {result.action === 'pay' && <CreditCard size={14} />}
          {result.action === 'appeal' && <Scale size={14} />}
          {result.action === 'investigate' && <Search size={14} />}
          Proceed to {result.action.charAt(0).toUpperCase() + result.action.slice(1)}
        </button>
      </div>
    </div>
  );
};

const emptyTecPrefill: TecFormPrefillValues = {
  fullName: '',
  firstName: '',
  lastName: '',
  company: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  email: '',
  phone: '',
  vrm: '',
  pcnNumber: '',
  submissionDate: '',
  submissionDay: '',
  submissionMonth: '',
  submissionYear: '',
  reason: '',
  statement: ''
};

const ComplianceCheck: React.FC<{ ticket: Ticket }> = ({ ticket }) => {
  const contraventionDate = new Date(ticket.contravention_date || ticket.issue_date);
  const issueDate = new Date(ticket.issue_date);
  const daysDiff = Math.floor((issueDate.getTime() - contraventionDate.getTime()) / (1000 * 3600 * 24));
  
  const isServiceLate = daysDiff > 28;
  const isLocationVague = !ticket.location || ticket.location.length < 5;

  return (
    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 p-4 mb-6">
      <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
        <Shield size={14} /> Statutory Compliance
      </h3>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isServiceLate ? <XCircle size={16} className="text-red-500" /> : <CheckCircle2 size={16} className="text-green-500" />}
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Service Timeliness</span>
          </div>
          <span className={`text-xs font-mono ${isServiceLate ? 'text-red-600 dark:text-red-400 font-bold' : 'text-slate-500 dark:text-slate-400'}`}>
            {daysDiff} Days {isServiceLate && '(Late)'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isLocationVague ? <AlertTriangle size={16} className="text-amber-500" /> : <CheckCircle2 size={16} className="text-green-500" />}
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Location Precision</span>
          </div>
          <span className="text-xs text-slate-700 dark:text-slate-300 truncate max-w-[120px]">
            {ticket.location || 'Missing'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-green-500" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Process Flow</span>
          </div>
          <span className="text-xs text-slate-700 dark:text-slate-300">Valid</span>
        </div>
      </div>
    </div>
  );
};

const StageTracker: React.FC<{ stage: string; enforcementType: EnforcementType }> = ({ stage, enforcementType }) => {
  const stages = enforcementType === 'private'
    ? [
        { id: 'PCN/NTK', label: 'PCN/NTK', keywords: ['parking charge notice', 'pcn', 'notice to keeper', 'ntk', 'notice to driver', 'ntd'] },
        { id: 'Reminder', label: 'Reminder', keywords: ['reminder'] },
        { id: 'Debt Recovery', label: 'Debt Recovery', keywords: ['debt recovery', 'debt collection'] },
        { id: 'Letter Before Claim', label: 'LBC', keywords: ['letter before claim', 'letter before action', 'lbc', 'lba'] },
        { id: 'Court Claim', label: 'Court', keywords: ['court claim', 'county court', 'claim form', 'claim number'] }
      ]
    : [
        { id: 'NtO/Postal PCN', label: 'NtO/Postal', keywords: ['pcn issued', 'penalty charge notice', 'pcn', 'notice to owner', 'nto', 'n.t.o', 'postal pcn'] },
        { id: 'Charge Certificate', label: 'Charge Cert', keywords: ['charge certificate', 'charge cert', 'cc'] },
        { id: 'Order for Recovery', label: 'OfR', keywords: ['order for recovery', 'te3', 'warrant', 'ofr'] }
      ];

  // Normalize and match stage flexibly
  const normalizedStage = normalizeStage(stage || (enforcementType === 'private' ? 'Parking Charge Notice' : 'PCN Issued'), enforcementType).toLowerCase().trim();
  const mergedStage = enforcementType === 'private'
    ? normalizedStage
    : (isPcnOrNtoStage(stage) ? 'nto/postal pcn' : normalizedStage);
  const currentIndex = stages.findIndex(s =>
    s.id.toLowerCase() === mergedStage ||
    s.keywords.some(kw => mergedStage.includes(kw) || kw.includes(mergedStage))
  );
  const activeIndex = currentIndex === -1 ? 0 : currentIndex;

  return (
    <div className="w-full bg-white dark:bg-slate-900/80 backdrop-blur-sm p-4 rounded-xl border border-slate-200 dark:border-slate-800 mb-6 shadow-sm">
      <div className="flex items-center justify-between relative">
        <div className="absolute left-0 right-0 top-1/2 h-1 bg-slate-100 dark:bg-slate-800 -z-0 rounded-full mx-8" />
        <div 
          className="absolute left-0 top-1/2 h-1 bg-primary-600 -z-0 rounded-full mx-8 transition-all duration-500" 
          style={{ width: `${(activeIndex / (stages.length - 1)) * 100}%` }}
        />
        {stages.map((s, idx) => {
          const isCompleted = idx <= activeIndex;
          const isCurrent = idx === activeIndex;
          return (
            <div key={s.id} className="relative z-10 flex flex-col items-center gap-2">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                  isCompleted 
                    ? 'bg-primary-600 border-primary-600 text-white' 
                    : 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-400 dark:text-slate-600'
                }`}
              >
                {isCompleted ? <CheckCircle2 size={16} /> : <div className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-700" />}
              </div>
              <span className={`text-xs font-medium ${isCurrent ? 'text-primary-600 dark:text-primary-400 font-bold' : 'text-slate-500 dark:text-slate-500'}`}>
                {s.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DecisionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  description: string;
  onClick: () => void;
  variant?: 'primary' | 'neutral' | 'outline';
  isActive?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}> = ({ title, icon, description, onClick, variant = 'neutral', isActive, disabled = false, disabledTooltip }) => {
  const baseClasses = "flex flex-col items-start p-5 rounded-xl border transition-all text-left w-full h-full relative overflow-hidden group";

  let colorClasses = "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md cursor-pointer";
  if (variant === 'primary') colorClasses = "bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md cursor-pointer";
  if (isActive) colorClasses = "bg-slate-900 dark:bg-primary-600 border-slate-900 dark:border-primary-500 text-white shadow-lg ring-2 ring-slate-900 dark:ring-primary-500 ring-offset-2 dark:ring-offset-slate-950 cursor-pointer";
  if (disabled) colorClasses = "bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 opacity-60 cursor-not-allowed";

  // Text color logic
  const titleColor = disabled ? 'text-slate-400 dark:text-slate-600' : isActive ? 'text-white' : variant === 'primary' ? 'text-blue-900 dark:text-blue-100' : 'text-slate-800 dark:text-white';
  const descColor = disabled ? 'text-slate-400 dark:text-slate-600' : isActive ? 'text-slate-300 dark:text-primary-100' : variant === 'primary' ? 'text-blue-700 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400';
  const iconBg = disabled
    ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-600'
    : isActive
      ? 'bg-white/10 text-white'
      : variant === 'primary'
        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 group-hover:text-primary-600 dark:group-hover:text-primary-400';

  const button = (
    <button
      className={`${baseClasses} ${colorClasses}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? disabledTooltip : undefined}
    >
      <div className={`p-2 rounded-lg mb-3 transition-colors ${iconBg}`}>
        {icon}
      </div>
      <h3 className={`font-bold text-lg mb-1 ${titleColor}`}>{title}</h3>
      <p className={`text-sm ${descColor}`}>{description}</p>
      {isActive && <div className="absolute top-2 right-2 text-white"><CheckCircle2 size={20} /></div>}
      {disabled && disabledTooltip && (
        <div className="absolute top-2 right-2 text-slate-400 dark:text-slate-600">
          <Lock size={16} />
        </div>
      )}
    </button>
  );

  return button;
};

const DeadlineCountdown: React.FC<{ deadlineDate: string }> = ({ deadlineDate }) => {
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [urgency, setUrgency] = useState<'safe' | 'warning' | 'critical'>('safe');

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const deadline = new Date(deadlineDate);
      const diff = deadline.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft('EXPIRED');
        setUrgency('critical');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else {
        setTimeLeft(`${minutes}m`);
      }

      setUrgency(days <= 2 ? 'critical' : days <= 7 ? 'warning' : 'safe');
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [deadlineDate]);

  const bgColor = urgency === 'critical' ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800' :
                  urgency === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800' :
                  'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800';

  const textColor = urgency === 'critical' ? 'text-red-900 dark:text-red-100' :
                    urgency === 'warning' ? 'text-amber-900 dark:text-amber-100' :
                    'text-green-900 dark:text-green-100';

  const iconColor = urgency === 'critical' ? 'text-red-600 dark:text-red-400' :
                    urgency === 'warning' ? 'text-amber-600 dark:text-amber-400' :
                    'text-green-600 dark:text-green-400';

  return (
    <div className={`p-3 rounded-lg border mb-4 ${bgColor}`}>
      <div className="flex items-center gap-2">
        <Clock className={`w-5 h-5 ${iconColor}`} />
        <div className="flex-1">
          <p className="text-xs font-semibold">Appeal Deadline</p>
          <p className={`text-lg font-bold ${textColor}`}>{timeLeft}</p>
        </div>
        {urgency === 'critical' && (
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
        )}
      </div>
    </div>
  );
};

const FileUploadRow: React.FC<{
  label: string;
  subLabel?: string;
  isComplete: boolean;
  onUpload: (file: File) => void;
  uploading: boolean;
  icon: React.ReactNode;
}> = ({ label, subLabel, isComplete, onUpload, uploading, icon }) => {
  const fileRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) onUpload(e.target.files[0]);
  };

  return (
    <div className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
      isComplete 
        ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700' 
        : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/50'
    }`}>
      <div className={isComplete ? "text-green-600 dark:text-green-500" : "text-red-400"}>
        {icon}
      </div>
      <div className="flex-1">
        <p className={`text-sm font-medium ${isComplete ? 'text-slate-700 dark:text-slate-300' : 'text-red-700 dark:text-red-400'}`}>{label}</p>
        {subLabel && <p className="text-[10px] text-slate-400 dark:text-slate-500">{subLabel}</p>}
      </div>
      <div>
        {isComplete ? (
          <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-bold bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded">
            <CheckCircle2 size={12} /> READY
          </span>
        ) : (
          <button 
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1 text-xs font-bold text-white bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 px-3 py-1.5 rounded shadow-sm transition-colors"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            UPLOAD
          </button>
        )}
        <input type="file" ref={fileRef} className="hidden" accept="image/*,.pdf" onChange={handleChange} />
      </div>
    </div>
  );
};

const CheckItem: React.FC<{ label: string, status: 'success' | 'error' | 'pending', detail?: string }> = ({ label, status, detail }) => (
  <div className="flex items-start gap-3 py-2">
    <div className={`mt-0.5 ${
      status === 'success' ? 'text-green-500' : 
      status === 'error' ? 'text-red-500' : 
      'text-slate-300 dark:text-slate-600'
    }`}>
      {status === 'success' ? <CheckCircle2 size={18} /> : status === 'error' ? <XCircle size={18} /> : <div className="w-4 h-4 rounded-full border-2 border-slate-200 dark:border-slate-600" />}
    </div>
    <div>
      <p className={`text-sm font-medium ${status === 'pending' ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-300'}`}>{label}</p>
      {detail && <p className="text-xs text-slate-700 dark:text-slate-300">{detail}</p>}
    </div>
  </div>
);

const CopyField: React.FC<{ label: string; value: string; required?: boolean }> = ({ label, value, required }) => {
  const { addToast } = useToast();
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    addToast(`Copied ${label}`, 'info');
  };

  return (
    <div className="flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-2 relative group hover:border-primary-400 dark:hover:border-primary-500 transition-colors">
      <div className="flex items-center gap-1 mb-1">
        <span className="text-[10px] text-slate-400 uppercase font-bold">{label}</span>
        {required && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 bg-red-500 text-white rounded">Required</span>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-mono font-bold text-slate-800 dark:text-slate-200 truncate pr-8">{value}</span>
        <button
          onClick={handleCopy}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-all opacity-0 group-hover:opacity-100"
          title="Copy"
        >
          <Copy size={14} />
        </button>
      </div>
    </div>
  );
};

// --- Main Page Component ---

const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const { checkAccess, promptUpgrade } = useSubscription();
  const { organization } = useBranding();
  const { profile, session } = useAuth();
  const hasAiAppeals = checkAccess('aiAppeals');
  const canTransferLiability = BusinessModelHelpers.canTransferLiability(organization);



  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  
  // UI State
  const [activeEvidenceTab, setActiveEvidenceTab] = useState<'document' | 'photos' | 'submissions'>('document');
  const [liabilityInfo, setLiabilityInfo] = useState<{ party: string; confidence: number; tier: number } | null>(null);

  // Modals
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isAppealModalOpen, setIsAppealModalOpen] = useState(false);
  const [isAppealChecklistOpen, setIsAppealChecklistOpen] = useState(false); // Phase 1.4
  const [isLiabilityModalOpen, setIsLiabilityModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isInvestigationModalOpen, setIsInvestigationModalOpen] = useState(false);
  const [isSubmissionWizardOpen, setIsSubmissionWizardOpen] = useState(false);
  const [submissionType, setSubmissionType] = useState<'appeal' | 'transfer'>('appeal');

  // Submissions tracking
  const [submissions, setSubmissions] = useState<Submission[]>([]);

  // Flow State
  const [paymentStep, setPaymentStep] = useState<'details' | 'confirm'>('details');
  const [transactionRef, setTransactionRef] = useState('');
  const [appealReason, setAppealReason] = useState('Loading');
  const [appealText, setAppealText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [appealStrategy, setAppealStrategy] = useState<{
    recommendedStrategy: string;
    successProbability: number;
    reasoning: string;
    requiredEvidence: string[];
    legalGrounds: string[];
  } | null>(null);
  const [loadingStrategy, setLoadingStrategy] = useState(false);
  const [packType, setPackType] = useState<'TMA_2004' | 'POFA_2012'>('TMA_2004');
  const [generatingPack, setGeneratingPack] = useState(false);
  const [packUrl, setPackUrl] = useState<string | null>(null);
  const [linkedHire, setLinkedHire] = useState<Hire | null>(null);
  const [linkedDriver, setLinkedDriver] = useState<Driver | null>(null);
  const [updatingAgreement, setUpdatingAgreement] = useState(false);
  const [agreementUpdateError, setAgreementUpdateError] = useState<string | null>(null);

  const openPrivateDocument = async (ref?: string | null) => {
    if (!ref) return;

    try {
      const url = await ApiService.resolveDocumentUrl(ref);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    }
  };

  // Real-time recommendations with deadline awareness
  const { recommendation } = useRealtimeRecommendations(ticket, linkedHire, linkedDriver, organization);
  const recoveryDecision = useMemo(() => {
    if (!ticket) return null;
    return recommendation?.recoveryDecision ?? RecoveryDecisionService.decideRecoveryForTicket(ticket, {
      linkedHire,
      linkedDriver
    });
  }, [ticket, linkedHire, linkedDriver, recommendation?.recoveryDecision]);

  // Determine if appeal is allowed based on business rules
  // Appeals are NOT allowed for vehicles with hire agreements (must use Transfer Liability instead)
  // Appeals ARE allowed for fleet-owned vehicles (pool vehicles or allocated drivers)
  // Also check FleetMind recommendation for blocked actions
  const canAppeal = useMemo(() => {
    if (!ticket) return false;

    // CRITICAL BUSINESS RULE: Check if ticket has linked hire agreement
    // If hire agreement exists, user MUST use Transfer Liability, NOT Appeal
    if (linkedHire?.agreement_url) {
      return false;
    }

    // Check if FleetMind has blocked appeal action
    if (recommendation?.blockedActions?.includes('appeal')) {
      return false;
    }

    // Allow appeal for fleet-owned vehicles (pool vehicles or allocated drivers)
    return true;
  }, [ticket, linkedHire, recommendation]);

  // Generate appropriate tooltip message explaining why appeal is blocked
  const appealBlockedReason = useMemo(() => {
    if (!ticket || canAppeal) return undefined;

    if (linkedHire?.agreement_url) {
      return "Cannot appeal hired vehicles. Use Transfer Liability instead.";
    }

    if (recommendation?.blockedActions?.includes('appeal')) {
      // FleetMind has blocked appeal - use the recommendation reason
      return `FleetMind recommends against appeal: ${recommendation.reason}`;
    }

    return "Appeal not available for this ticket.";
  }, [ticket, canAppeal, linkedHire, recommendation]);

  const appealDeadlineDate = useMemo(() => {
    if (!ticket?.issue_date) return undefined;
    const base = new Date(ticket.issue_date);
    if (Number.isNaN(base.getTime())) return undefined;
    return new Date(base.getTime() + 28 * 24 * 60 * 60 * 1000);
  }, [ticket?.issue_date]);

  const driverNoticeType = useMemo(
    () => DriverNotificationService.getDriverTicketNoticeType(organization),
    [organization]
  );

  const driverNoticeSubject = useMemo(() => {
    if (!ticket) return '';
    return DriverNotificationService.getDriverTicketNoticeSubject(ticket, driverNoticeType);
  }, [ticket, driverNoticeType]);

  const [generationSteps, setGenerationSteps] = useState<string[]>([]);
  type HireSuggestion = Hire & {
    match_score?: number;
    match_label?: string;
    compliance_status?: 'Compliant' | 'At Risk' | 'Missing';
  };
  const [suggestedHires, setSuggestedHires] = useState<HireSuggestion[]>([]);
  const [otherHires, setOtherHires] = useState<Hire[]>([]);
  const [hireSearchTerm, setHireSearchTerm] = useState('');
  const [loadingHires, setLoadingHires] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreateInvoiceModalOpen, setIsCreateInvoiceModalOpen] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [liabilityConfirmed, setLiabilityConfirmed] = useState(false); // User checkbox

  // No Hire Confirmation state
  const [confirmingNoHire, setConfirmingNoHire] = useState(false);
  const [confirmingNoHireLoading, setConfirmingNoHireLoading] = useState(false);
  const [noHireReason, setNoHireReason] = useState<'hire_not_yet_uploaded' | 'vehicle_company_owned' | 'third_party_error' | 'other' | ''>('');
  const [noHireNote, setNoHireNote] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [submittingAppeal, setSubmittingAppeal] = useState(false);
  const [appealOutcomeModal, setAppealOutcomeModal] = useState<{ open: boolean; outcome: 'appeal_won' | 'appeal_lost' } | null>(null);
  const [isOfrAssessmentOpen, setIsOfrAssessmentOpen] = useState(false);
  const [ofrReceiptStatus, setOfrReceiptStatus] = useState<'received' | 'not_received' | null>(null);
  const [ofrTruthAcknowledged, setOfrTruthAcknowledged] = useState(false);
  const [formPrefill, setFormPrefill] = useState<TecFormPrefillValues>(emptyTecPrefill);
  const [formPrefillTouched, setFormPrefillTouched] = useState(false);
  const [downloadingForm, setDownloadingForm] = useState<null | 'primary' | 'outOfTime'>(null);
  const [isTecSubmitOpen, setIsTecSubmitOpen] = useState(false);
  const [isTeFormWizardOpen, setIsTeFormWizardOpen] = useState(false);
  const [teFormPdfBytes, setTeFormPdfBytes] = useState<{ te9?: Uint8Array; te7?: Uint8Array } | null>(null);
  const [tecEmailTo, setTecEmailTo] = useState('tec@justice.gov.uk');
  const [tecEmailSubject, setTecEmailSubject] = useState('');
  const [tecStatement, setTecStatement] = useState('');
  const [tecReplyTo, setTecReplyTo] = useState('');
  const [tecIncludeOutOfTime, setTecIncludeOutOfTime] = useState(false);
  const [tecAttachmentFiles, setTecAttachmentFiles] = useState<File[]>([]);
  const [tecFileInputKey, setTecFileInputKey] = useState(0);
  const [tecPeChecklist, setTecPeChecklist] = useState({
    signed: false,
    scanned: false,
    legible: false,
  });
  const [tecCloseWarning, setTecCloseWarning] = useState(false);
  const [tecSending, setTecSending] = useState(false);
  const [tecError, setTecError] = useState<string | null>(null);
  const [tecSubjectTouched, setTecSubjectTouched] = useState(false);
  const [tecReplyTouched, setTecReplyTouched] = useState(false);
  const [tecStatementTouched, setTecStatementTouched] = useState(false);
  const [tecSignatureDataUrl, setTecSignatureDataUrl] = useState<string | null>(null);
  const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
  const signatureContextRef = useRef<CanvasRenderingContext2D | null>(null);
  const agreementReplaceRef = useRef<HTMLInputElement>(null);
  const [tecReasonConfirmed, setTecReasonConfirmed] = useState(false);

  const readTimeline = (t: any): any[] => {
    const raw = t?.timeline ?? t;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const initSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.clearRect(0, 0, rect.width, rect.height);
    signatureContextRef.current = ctx;

    if (tecSignatureDataUrl) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, rect.width, rect.height);
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = tecSignatureDataUrl;
    }
  };

  // Evidence Hub
  const evidenceInputRef = useRef<HTMLInputElement>(null);
  const [scanSignedUrl, setScanSignedUrl] = useState<string>('');
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [evidenceItems, setEvidenceItems] = useState<Array<{ name: string; path: string; signedUrl?: string }>>([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [isDriverNoticeModalOpen, setIsDriverNoticeModalOpen] = useState(false);
  const [driverNoticeRecipient, setDriverNoticeRecipient] = useState<DriverTicketRecipient | null>(null);
  const [driverNoticeNote, setDriverNoticeNote] = useState('');
  const [sendingDriverNotice, setSendingDriverNotice] = useState(false);

  // Liability Transfer Flow
  const [transferStep, setTransferStep] = useState<'check' | 'pack' | 'helper' | 'proof' | 'done'>('check');
  const [submissionProofFile, setSubmissionProofFile] = useState<File | null>(null);
  const [, setTransferProofPath] = useState<string | null>(null);
  const [transferSubmittedAt, setTransferSubmittedAt] = useState<string | null>(null);
  const [representationText, setRepresentationText] = useState<string>('');
  const [templateVariant, setTemplateVariant] = useState<TemplateVariant>('formal');
  const [transferReferenceNumber, setTransferReferenceNumber] = useState<string>('');
  const [detectedTransferAuthority, setDetectedTransferAuthority] = useState<any>(null);

  useEffect(() => {
    if (id) loadTicket(id);
  }, [id]);

  // Load submissions when ticket loads
  useEffect(() => {
    if (!ticket?.id) return;
    const loadSubmissions = async () => {
      try {
        const subs = await getTicketSubmissions(ticket.id);
        setSubmissions(subs);
      } catch (error) {
        console.error('Failed to load submissions:', error);
      }
    };
    loadSubmissions();
  }, [ticket?.id]);

  // Generate representation text for liability transfer using selected template
  const generateRepresentationText = (variant: TemplateVariant): string => {
    if (!ticket || !linkedHire) return '';
    return generateRepresentation(ticket, linkedHire, linkedDriver || null, variant, organization?.name);
  };

  // Initialize representation text when entering helper step or changing template
  useEffect(() => {
    if (transferStep === 'helper' && ticket && linkedHire) {
      const text = generateRepresentationText(templateVariant);
      if (text && !representationText) {
        setRepresentationText(text);
      }
    }
  }, [transferStep, ticket, linkedHire, templateVariant]);

  // Find authority data and portal URL for submission helper
  const authorityData = React.useMemo(() => {
    if (!ticket?.issuing_authority) return null;
    return findAuthorityByText(ticket.issuing_authority);
  }, [ticket?.issuing_authority]);

  const portalUrl = React.useMemo(() => {
    if (!authorityData) return null;
    const portalMethod = authorityData.submissionMethods?.find(m => m.type === 'portal');
    return portalMethod?.url || null;
  }, [authorityData]);

  useEffect(() => {
    if (!ticket) return;
    if (activeEvidenceTab !== 'document') return;

    const ref = ticket.document_url || '';
    if (!ref) {
      setScanSignedUrl('');
      setScanError(null);
      return;
    }

    let active = true;
    setScanLoading(true);
    setScanError(null);
    ApiService.resolveDocumentUrl(ref)
      .then((url) => {
        if (!active) return;
        setScanSignedUrl(url);
      })
      .catch((err) => {
        if (!active) return;
        setScanError(getErrorMessage(err));
        setScanSignedUrl('');
      })
      .finally(() => {
        if (!active) return;
        setScanLoading(false);
      });

    return () => { active = false; };
  }, [activeEvidenceTab, ticket]);

  useEffect(() => {
    if (!ticket) return;
    if (activeEvidenceTab !== 'photos') return;
    if (canTransferLiability) return;

    let active = true;
    setEvidenceLoading(true);
    setEvidenceError(null);
    ApiService.listTicketEvidence(ticket.id)
      .then((items) => {
        if (!active) return;
        setEvidenceItems(items);
      })
      .catch((err) => {
        if (!active) return;
        setEvidenceError(getErrorMessage(err));
      })
      .finally(() => {
        if (!active) return;
        setEvidenceLoading(false);
      });

    return () => { active = false; };
  }, [activeEvidenceTab, ticket]);

  useEffect(() => {
    if (canTransferLiability && activeEvidenceTab !== 'document') setActiveEvidenceTab('document');
  }, [activeEvidenceTab, canTransferLiability]);

  // Auto-detect authority when transfer modal opens
  useEffect(() => {
    if (isLiabilityModalOpen && ticket) {
      const authority = detectAuthority({
        issuing_authority: ticket.issuing_authority,
        location: ticket.location,
        pcn_number: ticket.pcn_number,
        contravention_type: ticket.contravention_type
      });
      setDetectedTransferAuthority(authority);
    }
  }, [isLiabilityModalOpen, ticket]);

  useEffect(() => {
    if (ticket) {
      if (ticket.hire_id) {
        fetchLinkedData(ticket.hire_id);
      } else {
        // No hire - try to fetch driver by assigned_driver_name
        if (ticket.assigned_driver_name && !linkedDriver) {
          fetchDriverForTicket(ticket.assigned_driver_name);
        }

        if (organization) {
          // Recommendation now handled by useRealtimeRecommendations hook
          loadLiabilityInfo();
        }
      }
    }
  }, [ticket, organization, linkedDriver]);

  useEffect(() => {
    if (linkedHire?.agreement_has_liability_clause) {
      setLiabilityConfirmed(true);
    } else {
      setLiabilityConfirmed(false);
    }
  }, [linkedHire]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('transfer') === '1') {
      setIsLiabilityModalOpen(true);
      setTransferStep('check');
      if (params.get('hireUpdated') === '1') {
        addToast('Hire agreement updated. Rechecking clause…', 'success');
      }
    }
  }, [location.search, addToast]);

  const fetchLinkedData = async (hireId: string) => {
    const hire = await ApiService.getHireById(hireId);
    setLinkedHire(hire ? { ...hire } : null);

    let driver = null;
    const hireDriver = hire ? ((Array.isArray((hire as any).drivers) ? (hire as any).drivers[0] : (hire as any).drivers) || null) : null;
    if (hireDriver && (hireDriver.address_line1 || hireDriver.postcode || hireDriver.email || hireDriver.phone)) {
      driver = hireDriver;
    }
    // Try to fetch driver by hire's driver_name first
    if (!driver && hire?.driver_name) {
      driver = await ApiService.getDriverByName(hire.driver_name);
    }
    // Fallback: try ticket's assigned_driver_name if hire lookup failed
    if (!driver && ticket?.assigned_driver_name) {
      driver = await ApiService.getDriverByName(ticket.assigned_driver_name);
    }
    setLinkedDriver(driver);

    // Recommendation now handled by useRealtimeRecommendations hook
  };

  // Fetch driver when ticket has assigned_driver_name but no hire
  const fetchDriverForTicket = async (driverName: string) => {
    if (linkedDriver) return; // Already have a driver
    const driver = await ApiService.getDriverByName(driverName);
    if (driver) {
      setLinkedDriver(driver);
      // Recommendation now handled by useRealtimeRecommendations hook
    }
  };

  const loadLiabilityInfo = async () => {
    if (!ticket || !organization) return;

    try {
      const result = await analyzeLiability(ticket.id, organization.id);
      if (result?.decision?.legal_result && result.decision.legal_confidence !== undefined && result.decision.evidence_tier) {
        // Normalize confidence to 0-1 range if it's already a percentage (0-100)
        const confidence = result.decision.legal_confidence > 1
          ? result.decision.legal_confidence / 100
          : result.decision.legal_confidence;

        setLiabilityInfo({
          party: result.decision.legal_result,
          confidence: confidence,
          tier: result.decision.evidence_tier
        });
      }
    } catch (err) {
      console.error('Error loading liability info:', err);
      // Silently fail - liability info is optional enhancement
    }
  };

  const loadTicket = async (ticketId: string) => {
    setLoading(true);
    try {
      const data = await ApiService.getTicket(ticketId);
      if (data) {
        setTicket(data);
        const vehicleRel = (data as any).vehicles;
        const v = Array.isArray(vehicleRel) ? vehicleRel[0] : vehicleRel;
        setVehicle(v || null);
        if (data.issuing_authority.includes('Council') || data.issuing_authority.includes('TfL')) {
          setPackType('TMA_2004');
        } else {
          setPackType('POFA_2012');
        }
      }
      else navigate('/tickets');
    } catch (err) {
      console.error(err);
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleRecommendationAction = (action: string) => {
    const stage = ticket?.stage ? normalizeStage(ticket.stage, enforcementType).toLowerCase() : '';
    const isOrderForRecovery = enforcementType === 'statutory' && (stage.includes('order for recovery') || stage.includes('te3') || stage.includes('ofr'));
    switch (action) {
      case 'transfer': setIsLiabilityModalOpen(true); break;
      case 'pay':
        if (isOrderForRecovery) {
          setOfrReceiptStatus(null);
          setOfrTruthAcknowledged(false);
          setIsOfrAssessmentOpen(true);
          break;
        }
        setIsPaymentModalOpen(true);
        break;
      case 'appeal':
        if (!canAppeal) {
          addToast('Cannot appeal hired vehicles. Use Transfer Liability instead.', 'error');
          break;
        }
        setAppealReason('Procedural');
        setIsAppealModalOpen(true);
        break;
      case 'investigate':
        setIsInvestigationModalOpen(true);
        break;
    }
  };

  const handleOpenCreateInvoice = () => {
    if (!recoveryDecision?.invoiceable) {
      addToast(
        recoveryDecision?.blockingReasons[0] ||
          recoveryDecision?.hint ||
          'This ticket is not ready for driver invoicing.',
        'error'
      );
      return;
    }
    setIsCreateInvoiceModalOpen(true);
  };

  const enforcementType = ticket
    ? inferEnforcementType({
        issuing_authority: ticket.issuing_authority,
        document_type: ticket.document_type,
        document_intent: ticket.document_intent,
        key_phrases: ticket.key_phrases,
        stage: ticket.stage
      })
    : 'statutory';
  const isStatutoryEnforcement = enforcementType === 'statutory';

  const stageKey = ticket?.stage ? normalizeStage(ticket.stage, enforcementType).toLowerCase() : '';
  const isOrderForRecoveryStage = isStatutoryEnforcement && (stageKey.includes('order for recovery') || stageKey.includes('te3') || stageKey.includes('ofr'));
  const tecForms = ticket && (isStatutoryEnforcement || isOrderForRecoveryStage)
    ? getTecFormRecommendation(ticket)
    : null;
  const statDecFormLabel = tecForms
    ? `${tecForms.primaryForm}${tecForms.isOutOfTime ? ` + ${tecForms.outOfTimeForm}` : ''}`
    : 'statutory declaration';
  const defaultTecSubject = ticket
    ? `Statutory declaration submission - ${ticket.pcn_number || ticket.vrn}`
    : 'Statutory declaration submission';
  const isPeChecklistComplete = tecPeChecklist.signed && tecPeChecklist.scanned && tecPeChecklist.legible;
  const defaultReason = ofrReceiptStatus === 'not_received'
    ? 'I did not receive the Notice to Owner or Charge Certificate.'
    : '';
  const reasonValue = formPrefill.reason?.trim() || '';
  const isReasonConfirmed = tecReasonConfirmed && reasonValue.length > 0;

  const buildPrefillValues = (): TecFormPrefillValues => {
    // Name resolution: driver record > ticket assignment > hire record
    const rawName = linkedDriver?.name || ticket?.assigned_driver_name || linkedHire?.driver_name || '';
    const nameParts = rawName.trim().split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear());

    // Email/phone: driver record > hire record > ticket assignment
    const email = linkedDriver?.email || linkedHire?.driver_email || ticket?.assigned_driver_email || '';
    const phone = linkedDriver?.phone || linkedHire?.driver_phone || '';

    return {
      fullName: rawName,
      firstName,
      lastName,
      company: organization?.name || '',
      addressLine1: linkedDriver?.address_line1 || '',
      addressLine2: '',
      city: linkedDriver?.city || '',
      postcode: linkedDriver?.postcode || '',
      email,
      phone,
      vrm: ticket?.vrn || '',
      pcnNumber: ticket?.pcn_number || '',
      submissionDate: `${day}/${month}/${year}`,
      submissionDay: day,
      submissionMonth: month,
      submissionYear: year,
      reason: defaultReason,
      statement: defaultReason
    };
  };

  useEffect(() => {
    if (!ticket) return;
    if (formPrefillTouched) return;
    setFormPrefill(buildPrefillValues());
  }, [ticket, linkedDriver, linkedHire, organization, formPrefillTouched]);

  useEffect(() => {
    if (!isOfrAssessmentOpen) return;
    if (!tecForms || tecForms.category !== 'traffic_enforcement_order') return;
    const handle = requestAnimationFrame(() => initSignatureCanvas());
    return () => cancelAnimationFrame(handle);
  }, [isOfrAssessmentOpen, tecForms?.category, tecSignatureDataUrl]);

  useEffect(() => {
    if (!ticket) return;
    if (!tecSubjectTouched) setTecEmailSubject(defaultTecSubject);
    if (!tecReplyTouched) setTecReplyTo(profile.email || session?.user?.email || '');
    setTecIncludeOutOfTime(tecForms?.isOutOfTime ?? false);
  }, [ticket, tecForms, defaultTecSubject, profile.email, session?.user?.email, tecSubjectTouched, tecReplyTouched]);

  useEffect(() => {
    if (!ticket) return;
    if (formPrefillTouched) return;
    if (!defaultReason) return;
    setFormPrefill((prev) => ({
      ...prev,
      reason: defaultReason,
      statement: defaultReason,
    }));
    setTecReasonConfirmed(false);
  }, [ticket, defaultReason, formPrefillTouched]);

  const updatePrefillValue = (key: keyof TecFormPrefillValues, value: string) => {
    setFormPrefillTouched(true);
    setFormPrefill((prev) => {
      if (key === 'reason') {
        setTecReasonConfirmed(false);
        return { ...prev, reason: value, statement: value };
      }
      if (key !== 'fullName') {
        return { ...prev, [key]: value };
      }
      const parts = value.trim().split(/\s+/).filter(Boolean);
      const firstName = parts[0] || '';
      const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';
      return { ...prev, fullName: value, firstName, lastName };
    });
  };

  useEffect(() => {
    if (tecStatementTouched) return;
    if (!formPrefill.reason) return;
    setTecStatement(formPrefill.reason);
  }, [formPrefill.reason, tecStatementTouched]);

  const handleMarkStatDecPath = async () => {
    if (!ticket) return;
    if (!ofrTruthAcknowledged) {
      addToast('Please confirm the declaration warning first.', 'error');
      return;
    }

    try {
      const existingTimeline = readTimeline(ticket);
      const now = new Date().toISOString();
      const forms = tecForms
        ? `${tecForms.primaryForm}${tecForms.isOutOfTime ? ` + ${tecForms.outOfTimeForm}` : ''}`
        : 'Statutory declaration / operator process';
      const outOfTimeNote = tecForms && !tecForms.isOutOfTime
        ? ` If you are out of time, also submit ${tecForms.outOfTimeForm}.`
        : '';

      const timelineEvent = {
        id: `ofr-statdec-${Date.now()}`,
        date: now,
        title: 'Statutory declaration pathway selected',
        description:
          `User indicated non-receipt of earlier notices at Order for Recovery stage. Next steps: ${forms}.${outOfTimeNote} ` +
          `WARNING: False declarations can be a criminal offence/perjury. Only proceed if the statement is true.`,
        type: 'system'
      };

      const nextTimeline = [...existingTimeline, timelineEvent];

      // Optimistic UI update so the user immediately sees progress.
      setTicket((prev) => (prev ? ({ ...prev, decision: 'appeal', timeline: nextTimeline as any } as any) : prev));
      setIsOfrAssessmentOpen(false);

      // Persist ticket update.
      await ApiService.updateTicket(ticket.id, {
        decision: 'appeal',
        timeline: nextTimeline as any
      });

      // Create a follow-up task so this doesn't disappear into "nothing happened".
      const dueBase = new Date(ticket.issue_date || now);
      const dueDate = Number.isNaN(dueBase.getTime()) ? undefined : new Date(dueBase.getTime() + 21 * 24 * 60 * 60 * 1000).toISOString();
      const tecLink = 'https://www.gov.uk/government/collections/traffic-enforcement-centre-forms';
      const formLinks = tecForms
        ? `Use official forms: ${tecForms.primaryLink}${tecForms.isOutOfTime ? ` and ${tecForms.outOfTimeLink}` : ''}.`
        : '';
      const description =
        `Order for Recovery: prepare and submit ${forms}. ` +
        `Only proceed if the non-receipt statement is true (false declarations can be perjury). ` +
        `${formLinks || (tecForms ? `Use official forms: ${tecLink}.` : '')}`;

      try {
        await ApiService.createTask({
          title: `Order for Recovery: file statutory declaration`,
          description,
          priority: 'High',
          type: 'appeal',
          relatedId: ticket.id,
          dueDate
        });
      } catch (taskErr) {
        console.warn('Failed to create stat dec task (non-blocking):', taskErr);
      }

      // Refresh from server so UI matches persisted state.
      const updated = await ApiService.getTicket(ticket.id);
      if (updated) setTicket(updated as any);
      addToast('Saved. Added to Action Inbox for follow-up.', 'success');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
      // Best-effort sync back to server state if optimistic update got ahead.
      try {
        const updated = await ApiService.getTicket(ticket.id);
        if (updated) setTicket(updated as any);
      } catch {
        // ignore
      }
    }
  };

  const handleDownloadTecForm = async (kind: 'primary' | 'outOfTime') => {
    if (!tecForms) return;
    const sourceUrl = kind === 'primary' ? tecForms.primaryLink : tecForms.outOfTimeLink;
    const formId = kind === 'primary' ? tecForms.primaryFormId : tecForms.outOfTimeFormId;
    if (!sourceUrl) return;

    setDownloadingForm(kind);
    try {
      const signatureDataUrl = tecForms.category === 'traffic_enforcement_order' ? (tecSignatureDataUrl || undefined) : undefined;
      const result = await fillTecPdf(sourceUrl, formPrefill, formId, { forceOverlay: true, signatureDataUrl });
      const suffix = kind === 'primary' ? tecForms.primaryForm.replace(/\s+/g, '') : tecForms.outOfTimeForm.replace(/\s+/g, '');
      const filename = `${suffix}_${ticket?.pcn_number || 'form'}.pdf`;
      downloadBytes(result.bytes, filename, 'application/pdf');
      if (!result.wasFilled && !result.usedOverlay) {
        addToast('Downloaded official form. This PDF has limited fillable fields—please review before submitting.', 'info');
      }
    } catch (err) {
      console.warn('Failed to prefill TEC form:', err);
      addToast('Could not prefill the official form. Opening the blank form instead.', 'info');
      window.open(sourceUrl, '_blank', 'noreferrer');
    } finally {
      setDownloadingForm(null);
    }
  };

  const toBase64 = (bytes: Uint8Array) => {
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  };

  const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read attachment'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.readAsDataURL(file);
  });

  const handleTecFileChange = (files: FileList | null) => {
    if (!files) return;
    setTecAttachmentFiles(Array.from(files));
  };

  const handleReplaceTecAttachments = () => {
    setTecAttachmentFiles([]);
    setTecFileInputKey((prev) => prev + 1);
  };

  const handleSendTecEmail = async () => {
    if (!ticket || !tecForms) return;
    if (isMockMode) {
      setTecError(
        'Email sending is disabled in demo mode. To enable email:\n' +
        '1. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file\n' +
        '2. Deploy the send-email Edge Function (npx supabase functions deploy send-email)\n' +
        '3. Restart the dev server\n\n' +
        'For now, download the forms manually and email tec@justice.gov.uk directly.'
      );
      return;
    }
    setTecSending(true);
    setTecError(null);
    try {
      if (!reasonValue) {
        setTecError('Add a reason before sending to TEC.');
        setTecSending(false);
        return;
      }
      if (!tecReasonConfirmed) {
        setTecError('Confirm the reason for this submission before sending.');
        setTecSending(false);
        return;
      }
      const attachments: Array<{ filename: string; content: string; type?: string }> = [];
      let formsLabel = tecForms.primaryForm;

      if (tecForms.category === 'penalty_charge_notice') {
        if (!tecAttachmentFiles.length) {
          setTecError('Upload the signed PE2/PE3 PDFs before sending (solicitor signature required).');
          setTecSending(false);
          return;
        }
        if (!isPeChecklistComplete) {
          setTecError('Confirm the PE form checklist before sending.');
          setTecSending(false);
          return;
        }
        const filePayloads = await Promise.all(
          tecAttachmentFiles.map(async (file) => ({
            filename: file.name,
            content: await fileToBase64(file),
            type: file.type || 'application/pdf'
          }))
        );
        attachments.push(...filePayloads);
        formsLabel = tecAttachmentFiles.map((file) => file.name).join(', ');
      } else {
        if (teFormPdfBytes?.te9 || teFormPdfBytes?.te7) {
          if (!teFormPdfBytes?.te9) {
            setTecError('Generate the TE9 form before sending to TEC.');
            setTecSending(false);
            return;
          }
          attachments.push({
            filename: `${tecForms.primaryForm.replace(/\s+/g, '')}_${ticket.pcn_number || ticket.vrn}.pdf`,
            content: toBase64(teFormPdfBytes.te9),
            type: 'application/pdf'
          });

          if (tecIncludeOutOfTime) {
            if (!teFormPdfBytes.te7) {
              setTecError('Generate the TE7 form (out of time) before sending to TEC.');
              setTecSending(false);
              return;
            }
            attachments.push({
              filename: `${tecForms.outOfTimeForm.replace(/\s+/g, '')}_${ticket.pcn_number || ticket.vrn}.pdf`,
              content: toBase64(teFormPdfBytes.te7),
              type: 'application/pdf'
            });
            formsLabel = `${tecForms.primaryForm} + ${tecForms.outOfTimeForm}`;
          }
        } else {
          if (!tecSignatureDataUrl) {
            setTecError('Add your signature before sending TE7/TE9 to TEC.');
            setTecSending(false);
            return;
          }
          const primary = await fillTecPdf(tecForms.primaryLink, formPrefill, tecForms.primaryFormId, {
            forceOverlay: true,
            signatureDataUrl: tecSignatureDataUrl
          });
          attachments.push({
            filename: `${tecForms.primaryForm.replace(/\s+/g, '')}_${ticket.pcn_number || ticket.vrn}.pdf`,
            content: toBase64(primary.bytes),
            type: 'application/pdf'
          });

          if (tecIncludeOutOfTime) {
            const oot = await fillTecPdf(tecForms.outOfTimeLink, formPrefill, tecForms.outOfTimeFormId, {
              forceOverlay: true,
              signatureDataUrl: tecSignatureDataUrl
            });
            attachments.push({
              filename: `${tecForms.outOfTimeForm.replace(/\s+/g, '')}_${ticket.pcn_number || ticket.vrn}.pdf`,
              content: toBase64(oot.bytes),
              type: 'application/pdf'
            });
            formsLabel = `${tecForms.primaryForm} + ${tecForms.outOfTimeForm}`;
          }
        }
      }

      const response = await EmailService.sendTecSubmissionEmail({
        to: tecEmailTo.trim(),
        subject: tecEmailSubject.trim() || defaultTecSubject,
        data: {
          contactName: formPrefill.fullName || organization?.name || '',
          contactEmail: tecReplyTo || profile.email || session?.user?.email || '',
          contactPhone: formPrefill.phone,
          pcnNumber: ticket.pcn_number,
          vrm: ticket.vrn,
          forms: formsLabel,
          statement: tecStatement.trim()
        },
        attachments,
        replyTo: tecReplyTo || undefined,
        userId: session?.user?.id,
        organisationId: organization?.id
      });

      if (!response.success) {
        // Handle specific error types with actionable messages
        const errorMsg = response.error || 'Failed to send email';
        if (errorMsg.includes('preferences') || errorMsg.includes('frequency')) {
          throw new Error('Email was blocked by user preferences. Check Settings > Email Preferences to enable transactional emails.');
        }
        if (errorMsg.includes('Resend')) {
          throw new Error(`Email service error: ${errorMsg}. The email provider may be temporarily unavailable. Please try again.`);
        }
        throw new Error(errorMsg);
      }

      const timelineEvent = {
        id: `tec-email-${Date.now()}`,
        date: new Date().toISOString(),
        title: 'TEC submission sent',
        description: `Email sent to ${tecEmailTo} with ${formsLabel}.`,
        type: 'email'
      };
      await ApiService.updateTicket(ticket.id, {
        decision: 'appeal',
        timeline: [...readTimeline(ticket), timelineEvent] as any
      });
      const updated = await ApiService.getTicket(ticket.id);
      if (updated) setTicket(updated as any);

      addToast('TEC submission sent', 'success');
      setIsTecSubmitOpen(false);
    } catch (err) {
      console.error('TEC email error:', err);
      const message = getErrorMessage(err);
      // Add actionable guidance based on error type
      if (message.includes('network') || message.includes('fetch') || message.includes('Failed to fetch')) {
        setTecError(`Network error: ${message}. Check your internet connection and try again.`);
      } else {
        setTecError(message);
      }
    } finally {
      setTecSending(false);
    }
  };

  const handleTecCloseRequest = () => {
    if (tecForms?.category === 'penalty_charge_notice' && tecAttachmentFiles.length > 0 && !isPeChecklistComplete) {
      setTecCloseWarning(true);
      return;
    }
    setTecCloseWarning(false);
    setIsTecSubmitOpen(false);
  };

  const handleInvestigationComplete = async (reportText: string, recommendedAction: 'pay' | 'appeal' | 'transfer') => {
    if (!ticket) return;
    try {
        const timelineEvent = {
            id: `inv-${Date.now()}`,
            date: new Date().toISOString(),
            title: 'Internal Investigation Report',
            description: reportText,
            type: 'system',
            user: 'Fleet Admin'
        };
        await ApiService.updateTicket(ticket.id, {
            timeline: [...(ticket.timeline || []), timelineEvent as any]
        });
        const updated = await ApiService.getTicket(ticket.id);
        if (updated) setTicket(updated);
        addToast("Investigation report saved", "success");
        if (recommendedAction === 'appeal') {
            if (!canAppeal) {
                addToast('Cannot appeal hired vehicles. Use Transfer Liability instead.', 'error');
            } else {
                setAppealReason('Cloned');
                setIsAppealModalOpen(true);
            }
        } else if (recommendedAction === 'transfer') {
            setIsLiabilityModalOpen(true);
        } else if (recommendedAction === 'pay') {
            setIsPaymentModalOpen(true);
        }
    } catch (err) {
        addToast("Failed to save report", "error");
    }
  };

  const handleConfirmPayment = async () => { 
    if (!ticket) return;
    const ref = transactionRef.trim();
    if (!ref) {
      addToast('Please enter a receipt / transaction reference.', 'error');
      return;
    }

    setRecordingPayment(true);
    try {
      const existingTimeline = readTimeline(ticket);

      const paidAt = new Date().toISOString();
      const timelineEvent = {
        id: `payment-${Date.now()}`,
        date: paidAt,
        title: 'Payment recorded',
        description: `Receipt / transaction ref: ${ref}`,
        type: 'payment'
      };

      await ApiService.updateTicket(ticket.id, {
        status: 'paid',
        decision: 'pay',
        timeline: [...existingTimeline, timelineEvent] as any
      });

      const updated = await ApiService.getTicket(ticket.id);
      if (updated) setTicket(updated as any);

      addToast('Payment recorded', 'success');
      setIsPaymentModalOpen(false);
      setPaymentStep('details');
      setTransactionRef('');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setRecordingPayment(false);
    }
  };
  const handleOpenSubmissionWizard = (type: 'appeal' | 'transfer') => {
    setSubmissionType(type);
    setIsSubmissionWizardOpen(true);
  };

  const handleSubmissionComplete = async () => {
    setIsSubmissionWizardOpen(false);
    // Reload submissions
    if (ticket?.id) {
      try {
        const subs = await getTicketSubmissions(ticket.id);
        setSubmissions(subs);
        addToast('Submission recorded successfully!', 'success');
      } catch (error) {
        console.error('Failed to reload submissions:', error);
      }
    }
  };

	  const handleGetAppealStrategy = async () => {
	    if (!ticket) return;
	    if (!hasAiAppeals) { promptUpgrade(); return; }
	    setLoadingStrategy(true);
	    try {
	      const strategy = await ClaudeService.suggestAppealStrategy(ticket);
	      setAppealStrategy(strategy);
	      addToast('AI strategy analysis complete', 'success');
	    } catch (err) {
	      addToast('Failed to analyze appeal strategy', 'error');
	    } finally {
	      setLoadingStrategy(false);
	    }
	  };

	  const handleGenerateAppeal = async () => {
	    if (!ticket) return;
	    if (!hasAiAppeals) { promptUpgrade(); return; }
	    setIsGenerating(true);
	    try {
	      const text = await ClaudeService.generateAppealLetter(ticket, appealReason);
	      setAppealText(text);

	      // Bill for appeal generation (billable action)
	      try {
	        const usageRecordId = await ApiService.billAppealGeneration(ticket.id, {
	          reason: appealReason,
	          source: 'ticket_detail'
	        });
	        if (usageRecordId) {
	          await ApiService.completeBilling(usageRecordId);
	          console.log('✅ Appeal generation billed successfully');
	        }
	      } catch (billingErr) {
	        console.warn('Failed to bill appeal generation:', billingErr);
	        // Don't fail the operation if billing fails
	      }

	      addToast('Appeal drafted by AI', 'success');
	    } catch (err) {
	      addToast('Failed to generate appeal', 'error');
	    } finally {
	      setIsGenerating(false);
	    }
	  };
  // Copy All Appeal Fields - Phase 1.2
  const handleCopyAllFields = async () => {
    if (!ticket) return;

    const formattedText = `
PCN NUMBER: ${ticket.pcn_number || 'N/A'}
VEHICLE REGISTRATION: ${ticket.vrn || 'N/A'}
DATE OF ISSUE: ${ticket.issue_date ? new Date(ticket.issue_date).toLocaleDateString('en-GB') : 'N/A'}
LOCATION: ${ticket.location || 'N/A'}
AMOUNT: £${ticket.amount ? ticket.amount.toFixed(2) : 'N/A'}
REASON FOR APPEAL: ${appealReason || 'N/A'}

APPEAL STATEMENT:
${appealText || 'No appeal text provided'}
    `.trim();

    try {
      await navigator.clipboard.writeText(formattedText);
      addToast('All appeal fields copied to clipboard!', 'success');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      addToast('Failed to copy to clipboard. Please try again.', 'error');
    }
  };

  // Show checklist before submitting appeal - Phase 1.4
  const handleSendAppeal = () => {
    if (!ticket) return;
    if (!appealText.trim()) {
      addToast('Please add your appeal text before submitting.', 'error');
      return;
    }

    // Open checklist modal instead of submitting directly
    setIsAppealChecklistOpen(true);
  };

  // Actually submit the appeal after checklist confirmation - Phase 1.4
  const handleConfirmAppealSubmission = async () => {
    if (!ticket) return;

    setIsAppealChecklistOpen(false);
    setSubmittingAppeal(true);

    try {
      const existingTimeline = readTimeline(ticket);

      const submittedAt = new Date().toISOString();
      const timelineEvent = {
        id: `appeal-${Date.now()}`,
        date: submittedAt,
        title: 'Appeal submitted',
        description: `Appeal submitted via council portal. Strategy: ${appealReason || '—'}.`,
        type: 'system'
      };

      await ApiService.updateTicket(ticket.id, {
        status: 'appeal_pending',
        decision: 'appeal',
        timeline: [...existingTimeline, timelineEvent] as any
      });

      const updated = await ApiService.getTicket(ticket.id);
      if (updated) setTicket(updated as any);

      addToast('Appeal marked as submitted', 'success');
      setIsAppealModalOpen(false);
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setSubmittingAppeal(false);
    }
  };

  const handleMarkAppealOutcome = async (outcome: 'appeal_won' | 'appeal_lost') => {
    if (!ticket) return;
    try {
      const existingTimeline = readTimeline(ticket);

      const resolvedAt = new Date().toISOString();
      const title = outcome === 'appeal_won' ? 'Appeal won' : 'Appeal lost';
      const timelineEvent = {
        id: `appeal-outcome-${Date.now()}`,
        date: resolvedAt,
        title,
        description: 'Outcome recorded by fleet admin.',
        type: 'system'
      };

      await ApiService.updateTicket(ticket.id, {
        status: outcome,
        decision: 'appeal',
        timeline: [...existingTimeline, timelineEvent] as any
      });

      const updated = await ApiService.getTicket(ticket.id);
      if (updated) setTicket(updated as any);
      addToast('Appeal outcome updated', 'success');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setAppealOutcomeModal(null);
    }
  };
  const handleUploadDocument = async (file: File, type: 'agreement' | 'license') => { 
    if (type === 'agreement' && !linkedHire?.id) {
      addToast('No hire record found for this ticket.', 'error');
      return;
    }

    setUploadingDoc(true);
    if (type === 'agreement') {
      setUpdatingAgreement(true);
      setAgreementUpdateError(null);
    }

    try {
      let clauseDetected: boolean | null = null;
      if (type === 'agreement') {
        try {
          const extracted = await ClaudeService.extractHireAgreementData(file);
          clauseDetected = extracted.has_liability_clause;
        } catch (err) {
          clauseDetected = null;
          addToast('Agreement uploaded, but clause could not be verified. Please review manually.', 'info');
        }
      }

      const uploadResult = await ApiService.uploadDocument(file, type, linkedHire?.id || '');

      if (type === 'agreement' && linkedHire?.id) {
        await ApiService.updateHireAgreement(linkedHire.id, {
          agreement_url: uploadResult.path,
          agreement_signed_at: new Date().toISOString(),
          agreement_has_liability_clause: clauseDetected ?? undefined
        });

        if (clauseDetected === true) {
          addToast('Agreement updated — liability clause detected.', 'success');
        } else if (clauseDetected === false) {
          addToast('Agreement updated, but liability clause not detected.', 'info');
        }
      }
    } catch (err) {
      const msg = getErrorMessage(err);
      if (type === 'agreement') setAgreementUpdateError(msg);
      addToast(msg, 'error');
    } finally {
      setUploadingDoc(false);
      if (type === 'agreement') setUpdatingAgreement(false);
      if (linkedHire) fetchLinkedData(linkedHire.id);
      if (agreementReplaceRef.current) agreementReplaceRef.current.value = '';
    }
  };

  const getFileExt = (name: string) => {
    const clean = name.split('?')[0];
    const last = clean.split('/').pop() || '';
    const idx = last.lastIndexOf('.');
    return idx >= 0 ? last.slice(idx + 1).toLowerCase() : '';
  };

  const isPdfRef = (ref: string) => getFileExt(ref) === 'pdf';
  const isImageRef = (ref: string) => ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(getFileExt(ref));

  const handleUploadEvidenceFiles = async (files: FileList | null) => {
    if (!ticket || !files?.length) return;
    setEvidenceUploading(true);
    setEvidenceError(null);
    try {
      for (const file of Array.from(files)) {
        await ApiService.uploadTicketEvidence(ticket.id, file);
      }
      const updated = await ApiService.listTicketEvidence(ticket.id);
      setEvidenceItems(updated);
      addToast('Evidence uploaded', 'success');
    } catch (err) {
      setEvidenceError(getErrorMessage(err));
      addToast(getErrorMessage(err), 'error');
    } finally {
      setEvidenceUploading(false);
    }
  };

  const handleDeleteEvidence = async (path: string) => {
    if (!ticket) return;
    try {
      await ApiService.deleteTicketEvidence(path);
      const updated = await ApiService.listTicketEvidence(ticket.id);
      setEvidenceItems(updated);
      addToast('Evidence removed', 'info');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    }
  };

  const handleOpenDriverNoticeModal = () => {
    if (!ticket) return;
    const recipient = DriverNotificationService.resolveDriverTicketRecipient(ticket, linkedHire, linkedDriver);
    if (!recipient) {
      addToast('No driver email found for this ticket.', 'info');
      return;
    }

    setDriverNoticeRecipient(recipient);
    setDriverNoticeNote(DriverNotificationService.getDefaultDriverTicketNoticeNote(driverNoticeType));
    setIsDriverNoticeModalOpen(true);
  };

  const handleSendDriverNotice = async () => {
    if (!ticket || !driverNoticeRecipient) return;

    setSendingDriverNotice(true);
    try {
      const payload = DriverNotificationService.buildDriverTicketNoticePayload(
        ticket,
        organization,
        driverNoticeRecipient,
        driverNoticeNote
      );

      const response = await EmailService.sendEmail({
        to: driverNoticeRecipient.email,
        subject: driverNoticeSubject,
        template: 'DriverTicketNoticeEmail',
        data: payload,
        replyTo: profile.email || session?.user?.email || undefined,
        userId: session?.user?.id,
        organisationId: organization?.id
      });

      if (!response.success) {
        addToast(response.error || 'Failed to send driver notice', 'error');
        return;
      }

      const event: TimelineEvent = {
        id: `driver-notice-${Date.now()}`,
        date: new Date().toISOString(),
        title: 'Driver notified',
        description: `${driverNoticeType === 'fleet_evidence_request' ? 'Evidence/context request' : 'PCN accountability notice'} sent to ${driverNoticeRecipient.email}.`,
        type: 'email'
      };
      const timeline = [...readTimeline(ticket), event];
      await ApiService.updateTicket(ticket.id, { timeline });
      setTicket({ ...ticket, timeline });
      setIsDriverNoticeModalOpen(false);
      addToast('Driver notice sent', 'success');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setSendingDriverNotice(false);
    }
  };
  const handleGenerateLiabilityPack = async () => {
    if (!ticket) return;
    setGeneratingPack(true);
    setGenerationSteps([]);
    const steps = ["Validating...", "Generating representation text...", "Creating PDF..."];
    for (let i = 0; i < steps.length; i++) {
       await new Promise(r => setTimeout(r, 600));
       setGenerationSteps(prev => [...prev, steps[i]]);
    }
    try {
      // Generate representation text using current template variant
      const generatedText = generateRepresentationText(templateVariant);
      setRepresentationText(generatedText);

      const url = await ApiService.generateLiabilityPack(ticket.id, packType, generatedText);
      setPackUrl(url);
      setTicket(prev => prev ? ({ ...prev, decision: 'transfer', liability_pack: { type: packType, status: 'generated', generated_at: new Date().toISOString(), url } }) : null);
      setTransferStep('helper');

      // Bill for liability transfer pack generation (billable action)
      try {
        const usageRecordId = await ApiService.billLiabilityTransfer(ticket.id, {
          pack_type: packType,
          source: 'ticket_detail'
        });
        if (usageRecordId) {
          await ApiService.completeBilling(usageRecordId);
          console.log('✅ Liability transfer pack billed successfully');
        }
      } catch (billingErr) {
        console.warn('Failed to bill liability transfer:', billingErr);
        // Don't fail the operation if billing fails
      }

      addToast("Pack generated", "success");
    } catch(err) { addToast("Failed", "error"); } finally { setGeneratingPack(false); }
  };
  const handleSubmitTransferProof = async () => {
    if (!ticket) return;

    // Mandatory file validation
    if (!submissionProofFile) {
      addToast('Proof of submission is required. Please upload a screenshot or receipt.', 'error');
      return;
    }

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(submissionProofFile.type)) {
      addToast('Please upload a valid image (PNG, JPG) or PDF file.', 'error');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (submissionProofFile.size > maxSize) {
      addToast('File size must be less than 10MB. Please upload a smaller file.', 'error');
      return;
    }

    setUploadingDoc(true);
    try {
      // Upload proof screenshot to submission-proofs bucket
      const proofUrl = await uploadProofScreenshot(ticket.id, submissionProofFile);
      const submittedAt = new Date().toISOString();

      // Create submission record in database
      await createSubmission({
        ticket_id: ticket.id,
        submission_type: 'transfer',
        method: detectedTransferAuthority?.portal_url ? 'portal' : 'other',
        proof_type: 'screenshot',
        proof_url: proofUrl,
        reference_number: transferReferenceNumber || undefined,
        notes: `Transfer pack submitted via ${detectedTransferAuthority?.name || ticket.issuing_authority} portal`
      });

      // Update ticket status
      await ApiService.updateTicket(ticket.id, {
        status: 'transferred',
        decision: 'transfer' as any
      });

      const updated = await ApiService.getTicket(ticket.id);
      if (updated) setTicket(updated as any);

      // Reload submissions to show in timeline
      const subs = await getTicketSubmissions(ticket.id);
      setSubmissions(subs);

      setTransferProofPath(proofUrl);
      setTransferSubmittedAt(submittedAt);
      setTransferStep('done');
      addToast('Transfer submission logged', 'success');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setUploadingDoc(false);
    }
  };
  const handleOpenAssignModal = async () => { 
    if (!ticket) return;
    setIsAssignModalOpen(true);
    setOtherHires([]);
    setHireSearchTerm('');
    setSuggestedHires([]);
    setLoadingHires(true);

    try {
      const targetDate = ticket.contravention_date || ticket.issue_date;
      const suggestions = await ApiService.suggestHiresForTicket({
        vrm: ticket.vrn,
        contraventionDate: targetDate,
        limit: 3
      });
      setSuggestedHires((suggestions || []) as any);
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setLoadingHires(false);
    }
  };

  const handleSearchHires = async (term: string) => { 
    setHireSearchTerm(term);
    try {
      const res = await ApiService.searchHires(term);
      setOtherHires(res);
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    }
  };
  const handleAssignHire = async (hire: Hire) => {
    setAssigning(true);
    try {
      if (!ticket?.id) return;
      const existingTimeline = readTimeline(ticket);

      const timelineEvent = {
        id: `assign-${Date.now()}`,
        date: new Date().toISOString(),
        title: 'Ticket Assigned',
        description: `Assigned to hire ${hire.hire_ref || hire.id} (${hire.driver_name})`,
        type: 'system'
      };

      await ApiService.updateTicket(ticket.id, {
        hire_id: hire.id,
        assigned_driver_name: hire.driver_name,
        assigned_driver_email: hire.driver_email || undefined,
        status: ticket.status === 'unassigned' ? 'active' : ticket.status,
        timeline: [...existingTimeline, timelineEvent] as any
      });

      setIsAssignModalOpen(false);
      await loadTicket(ticket.id);
      addToast('Ticket assigned', 'success');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setAssigning(false);
    }
  };

  // Handle "Confirm No Hire" - two-step confirmation flow
  const handleConfirmNoHire = async () => {
    if (!ticket?.id || !noHireReason) return;
    setConfirmingNoHireLoading(true);
    try {
      await ApiService.confirmNoHire(
        ticket.id,
        noHireReason as any,
        noHireNote || undefined
      );
      setIsAssignModalOpen(false);
      setConfirmingNoHire(false);
      setNoHireReason('');
      setNoHireNote('');
      await loadTicket(ticket.id);
      addToast('No hire confirmed - ticket updated', 'success');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setConfirmingNoHireLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!ticket?.id) return;
    setIsDeleting(true);
    try {
      await ApiService.deleteTicket(ticket.id);
      addToast('Ticket deleted', 'success');
      navigate('/tickets');
    } catch (err) {
      addToast(getErrorMessage(err), 'error');
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  if (loading || !ticket) return <div className="h-screen flex items-center justify-center dark:bg-slate-950 dark:text-white">Loading...</div>;

  const statusKey = normalizeTicketStatus(ticket.status);
  const timeline = readTimeline(ticket);

  const lastPaymentEvent = [...timeline].reverse().find((e: any) => String(e?.type || '').toLowerCase() === 'payment' || String(e?.title || '').toLowerCase().includes('payment'));
  const lastPaymentRef = (() => {
    const text = String(lastPaymentEvent?.description || '');
    const m = text.match(/ref:\s*(.+)$/i);
    if (m?.[1]) return m[1].trim();
    const m2 = text.match(/transaction\s+ref:\s*(.+)$/i);
    if (m2?.[1]) return m2[1].trim();
    return text.includes(':') ? text.split(':').slice(1).join(':').trim() : '';
  })();

  const lastTransferEvent = [...timeline].reverse().find((e: any) => String(e?.title || '').toLowerCase().includes('liability transfer submitted'));
  const lastTransferPath = (() => {
    const text = String(lastTransferEvent?.description || '');
    const m = text.match(/\(path:\s*([^)]+)\)/i);
    return m?.[1]?.trim() || '';
  })();

  const isOrderForRecovery = isOrderForRecoveryStage;
  const lastStatDecEvent = [...timeline].reverse().find((e: any) => String(e?.title || '').toLowerCase().includes('statutory declaration'));

  const isBusLane = ticket.contravention_type?.toLowerCase().includes('bus lane');
  const isTransferBlocked = isBusLane && ticket.issuing_authority.toLowerCase().includes('london');

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/tickets')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-500 dark:text-slate-400 transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
               <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{ticket.pcn_number}</h1>
               <span className={`px-2 py-0.5 rounded text-sm font-bold border ${
                 statusKey === 'paid' ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' :
                 statusKey === 'appeal_pending' ? 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800' :
                 statusKey === 'transferred' ? 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800' :
                 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
               }`}>
                 {formatTicketStatus(ticket.status)}
               </span>
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm">{ticket.vrn} • {ticket.issuing_authority}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700">
             <Clock size={14} />
             Issued: {new Date(ticket.issue_date).toLocaleDateString('en-GB')}
          </div>
          <button
            type="button"
            onClick={() => setIsDeleteModalOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors text-sm font-medium"
            title="Delete ticket"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0">
        
        {/* Left: Evidence Hub */}
        <div className="lg:col-span-5 flex flex-col h-full bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm transition-all">
           {/* Evidence Tabs */}
           <div className="flex border-b border-slate-100 dark:border-slate-800">
             <button
               onClick={() => setActiveEvidenceTab('document')}
               className={`py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                 activeEvidenceTab === 'document'
                   ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 bg-primary-50 dark:bg-primary-900/10'
                   : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
               } ${canTransferLiability ? 'flex-1' : 'flex-1'}`}
             >
               <FileText size={16} /> Scan
             </button>
             {!canTransferLiability && (
               <button
                 onClick={() => setActiveEvidenceTab('photos')}
                 className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                   activeEvidenceTab === 'photos'
                     ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 bg-primary-50 dark:bg-primary-900/10'
                     : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                 }`}
               >
                 <ImageIcon size={16} /> Evidence
               </button>
             )}
             <button
               onClick={() => setActiveEvidenceTab('submissions')}
               className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                 activeEvidenceTab === 'submissions'
                   ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 bg-primary-50 dark:bg-primary-900/10'
                   : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
               }`}
             >
               <Send size={16} /> Submissions {submissions.length > 0 && `(${submissions.length})`}
             </button>
           </div>

           {/* Content Area */}
           <div className="flex-1 relative bg-slate-100 dark:bg-slate-950 flex items-center justify-center overflow-hidden">
              {activeEvidenceTab === 'document' && (
                <div className="w-full h-full flex flex-col">
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-700 dark:text-slate-300">Original PCN Scan</div>
                      <div className="text-sm font-bold text-slate-900 dark:text-white truncate">
                        {ticket.document_url ? ((ticket.document_url.split('/').pop() || 'Document').split('?')[0]) : 'No scan uploaded yet'}
                      </div>
                      {scanError && <div className="text-[11px] text-red-600 dark:text-red-400 mt-1 truncate">{scanError}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {scanSignedUrl && (
                        <a
                          href={scanSignedUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors inline-flex items-center gap-2"
                        >
                          <ExternalLink size={14} /> Open
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 bg-slate-50 dark:bg-slate-950/40">
                    {scanLoading ? (
                      <div className="h-full w-full flex items-center justify-center text-slate-500 dark:text-slate-400">
                        <Loader2 className="animate-spin" />
                      </div>
                    ) : scanSignedUrl ? (
                      isPdfRef(ticket.document_url || scanSignedUrl) ? (
                        <iframe title="PCN Scan" src={scanSignedUrl} className="w-full h-full" />
                      ) : isImageRef(ticket.document_url || scanSignedUrl) ? (
                        <div className="w-full h-full p-4 flex items-center justify-center">
                          <img
                            src={scanSignedUrl}
                            alt="PCN scan"
                            className="max-w-full max-h-full object-contain rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900"
                          />
                        </div>
                      ) : (
                        <div className="h-full w-full flex flex-col items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
                          <FileText className="w-12 h-12" />
                          <div className="text-sm font-medium">Preview not available</div>
                          <a
                            href={scanSignedUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary-600 dark:text-primary-400 text-sm font-bold hover:underline inline-flex items-center gap-2"
                          >
                            <Download size={16} /> Download / Open
                          </a>
                        </div>
                      )
                    ) : (
                      <div className="h-full w-full flex flex-col items-center justify-center opacity-70 gap-2">
                        <FileText className="w-16 h-16 text-slate-400 dark:text-slate-600" />
                        <p className="font-medium text-slate-600 dark:text-slate-400">No scan attached to this ticket.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeEvidenceTab === 'photos' && !canTransferLiability && (
                <div className="w-full h-full flex flex-col">
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-700 dark:text-slate-300">
                        Evidence
                      </div>
                      <div className="text-sm font-bold text-slate-900 dark:text-white truncate">
                        Upload photos, delivery notes, receipts, or supporting PDFs.
                      </div>
                      {evidenceError && <div className="text-[11px] text-red-600 dark:text-red-400 mt-1 truncate">{evidenceError}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <input
                        ref={evidenceInputRef}
                        type="file"
                        multiple
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          void handleUploadEvidenceFiles(e.target.files);
                          e.currentTarget.value = '';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => evidenceInputRef.current?.click()}
                        disabled={evidenceUploading}
                        className="px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                      >
                        {evidenceUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        Upload
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenDriverNoticeModal}
                        className="px-3 py-2 rounded-lg bg-primary-600 text-white text-xs font-bold hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
                      >
                        <Send size={14} /> Send to Driver
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto p-4">
                    {evidenceLoading ? (
                      <div className="h-full w-full flex items-center justify-center text-slate-500 dark:text-slate-400">
                        <Loader2 className="animate-spin" />
                      </div>
                    ) : evidenceItems.length === 0 ? (
                      <div className="h-full w-full flex flex-col items-center justify-center text-center p-8">
                        <div className="w-16 h-16 bg-slate-200 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3">
                          <ImageIcon className="text-slate-400 dark:text-slate-500" />
                        </div>
                        <h3 className="font-bold text-slate-700 dark:text-slate-300">
                          No evidence uploaded
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-500 mb-4 max-w-xs mx-auto">
                          Upload evidence files here, or click Request to email the assigned driver.
                        </p>
                        <button
                          type="button"
                          onClick={() => evidenceInputRef.current?.click()}
                          className="text-primary-600 dark:text-primary-400 text-sm font-bold hover:underline inline-flex items-center gap-2"
                        >
                          <Upload size={16} /> Upload evidence
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        {evidenceItems.map((item) => {
                          const url = item.signedUrl || '';
                          const isImage = isImageRef(item.name);
                          const isPdf = isPdfRef(item.name);
                          return (
                            <div
                              key={item.path}
                              className="group relative rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
                            >
                              <div className="h-28 bg-slate-50 dark:bg-slate-950/40 flex items-center justify-center overflow-hidden">
                                {isImage && url ? (
                                  <img src={url} alt={item.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                                    {isPdf ? <FileText size={18} /> : <ImageIcon size={18} />}
                                    <span className="text-xs font-medium">{isPdf ? 'PDF' : 'File'}</span>
                                  </div>
                                )}
                              </div>
                              <div className="p-3">
                                <div className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{item.name}</div>
                                <div className="mt-2 flex items-center gap-2">
                                  {url && (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs font-bold text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                                    >
                                      <ExternalLink size={12} /> Open
                                    </a>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteEvidence(item.path)}
                                    className="ml-auto text-xs font-bold text-red-600 dark:text-red-400 hover:underline inline-flex items-center gap-1"
                                    title="Remove"
                                  >
                                    <Trash2 size={12} /> Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeEvidenceTab === 'submissions' && (
                <div className="w-full h-full flex flex-col">
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur">
                    <div className="text-xs text-slate-700 dark:text-slate-300">Submission History</div>
                    <div className="text-sm font-bold text-slate-900 dark:text-white">
                      Track all appeal and transfer submissions
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto p-4">
                    <SubmissionsTimeline submissions={submissions} />
                  </div>
                </div>
              )}
           </div>
        </div>

        {/* Right: Management Console */}
        <div className="lg:col-span-7 flex flex-col gap-6 overflow-y-auto pr-2">
          
          <StageTracker stage={ticket.stage} enforcementType={enforcementType} />

          {(statusKey === 'paid' ||
            statusKey === 'transferred' ||
            statusKey === 'appeal_pending' ||
            statusKey === 'appeal_won' ||
            statusKey === 'appeal_lost' ||
            statusKey === 'appeal_rejected' ||
            statusKey === 'cancelled') && (
            <div
              className={`rounded-xl border p-4 shadow-sm ${
                statusKey === 'paid'
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-900/40'
                  : statusKey === 'transferred'
                    ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-900/40'
                    : statusKey === 'appeal_pending' || statusKey.startsWith('appeal_')
                      ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-900/40'
                      : 'bg-slate-50 dark:bg-slate-900/30 border-slate-200 dark:border-slate-800'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {statusKey === 'paid' && <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" />}
                    {statusKey === 'transferred' && <Briefcase size={18} className="text-indigo-600 dark:text-indigo-300" />}
                    {(statusKey === 'appeal_pending' || statusKey.startsWith('appeal_')) && (
                      <Scale size={18} className="text-purple-600 dark:text-purple-300" />
                    )}
                    {(statusKey === 'cancelled') && <XCircle size={18} className="text-slate-600 dark:text-slate-300" />}
                    <div className="text-sm font-bold text-slate-900 dark:text-white">
                      {formatTicketStatus(ticket.status)}
                    </div>
                  </div>
                  <div className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {statusKey === 'paid' && (
                      <>
                        Payment recorded{lastPaymentRef ? ` (ref: ${lastPaymentRef})` : ''}. This ticket is now closed.
                      </>
                    )}
                    {statusKey === 'transferred' && (
                      <>Liability transfer submitted. This ticket is now closed.</>
                    )}
                    {statusKey === 'appeal_pending' && (
                      <>Appeal submitted. Mark the outcome when the council responds.</>
                    )}
                    {statusKey === 'appeal_won' && (
                      <>Appeal won and the case is closed.</>
                    )}
                    {(statusKey === 'appeal_lost' || statusKey === 'appeal_rejected') && (
                      <>Appeal lost. You can proceed to payment if required.</>
                    )}
                    {statusKey === 'cancelled' && (
                      <>Case cancelled and closed.</>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {statusKey === 'appeal_pending' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setAppealOutcomeModal({ open: true, outcome: 'appeal_won' })}
                        className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-purple-200 dark:border-purple-800 text-xs font-bold text-purple-700 dark:text-purple-300 hover:bg-purple-100/60 dark:hover:bg-purple-900/30 transition-colors"
                      >
                        Mark Won
                      </button>
                      <button
                        type="button"
                        onClick={() => setAppealOutcomeModal({ open: true, outcome: 'appeal_lost' })}
                        className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        Mark Lost
                      </button>
                    </>
                  )}

                  {statusKey === 'transferred' && lastTransferPath && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const url = await ApiService.resolveDocumentUrl(lastTransferPath);
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } catch (e) {
                          addToast(getErrorMessage(e), 'error');
                        }
                      }}
                      className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 text-xs font-bold text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100/60 dark:hover:bg-indigo-900/30 transition-colors inline-flex items-center gap-2"
                      title="Open transfer proof"
                    >
                      <ExternalLink size={14} /> Proof
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* FleetMind AI Recommendation */}
          {statusKey !== 'paid' && statusKey !== 'appeal_pending' && (
            <RecommendationBanner
              result={recommendation}
              onApply={handleRecommendationAction}
              liabilityInfo={liabilityInfo}
            />
          )}

          {/* Compliance Check */}
          <ComplianceCheck ticket={ticket} />

          {/* Driver Invoice Status */}
          {(ticket.driver_charge_status || (recoveryDecision && recoveryDecision.action !== 'none')) && (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 mb-6">
              <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Receipt size={14} /> Driver Billing
              </h3>
              {ticket.driver_invoice_id ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-bold uppercase border ${
                      ticket.driver_charge_status === 'paid'
                        ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                        : ticket.driver_charge_status === 'invoiced'
                        ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800'
                        : 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                    }`}>
                      <Receipt size={12} />
                      {ticket.driver_charge_status === 'paid' ? 'Paid' : ticket.driver_charge_status === 'invoiced' ? 'Invoiced' : 'Pending'}
                    </span>
                    <div className="text-sm text-slate-600 dark:text-slate-300">
                      <span className="font-medium">£{ticket.driver_charge_amount || ticket.amount}</span>
                      {ticket.admin_fee_charged && ticket.admin_fee_charged > 0 && (
                        <span className="text-slate-400 dark:text-slate-500 ml-1">(+£{ticket.admin_fee_charged} admin)</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/driver-invoices?invoice=${ticket.driver_invoice_id}`)}
                    className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-medium rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                  >
                    View Invoice <ArrowRight size={12} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    <span className="font-medium text-slate-800 dark:text-slate-200">{recoveryDecision?.label || 'Driver Recovery'}</span>
                    {recoveryDecision?.hint ? ` - ${recoveryDecision.hint}` : ''}
                  </p>
                  {recoveryDecision?.invoiceable && (
                    <button
                      onClick={handleOpenCreateInvoice}
                      className="px-4 py-2 bg-primary-600 text-white text-xs font-bold rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                    >
                      <Receipt size={14} />
                      Create Invoice
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

	          {/* Decision Console */}
	          <section>
	            <h2 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
	              <Gavel size={16} /> Decision Console
	            </h2>
	            {isOrderForRecovery && lastStatDecEvent && (
	              <div className="mb-4 rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4">
	                <div className="flex items-start justify-between gap-3">
	                  <div className="min-w-0">
	                    <div className="text-sm font-bold text-purple-900 dark:text-purple-100">
	                      Statutory declaration route selected
	                    </div>
                    <div className="mt-1 text-sm text-purple-800 dark:text-purple-200">
                      Added to Action Inbox for follow-up. Submit {statDecFormLabel} as soon as possible and only if the non-receipt statement is true.
                    </div>
	                  </div>
	                  {isStatutoryEnforcement && (
	                    <a
	                      href="https://www.gov.uk/government/collections/traffic-enforcement-centre-forms"
	                      target="_blank"
	                      rel="noreferrer"
	                      className="shrink-0 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-purple-200 dark:border-purple-800 text-xs font-bold text-purple-700 dark:text-purple-200 hover:bg-purple-100/60 dark:hover:bg-purple-900/30 transition-colors inline-flex items-center gap-2"
	                    >
	                      <ExternalLink size={14} /> TEC Forms
	                    </a>
	                  )}
	                </div>
	              </div>
	            )}
	            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
	              <DecisionCard 
	                title="Pay Now" 
	                icon={<CreditCard size={20} />} 
	                description={`Pay Council: £${ticket.amount}`}
	                isActive={statusKey === 'paid' || ticket.decision === 'pay'}
	                onClick={() => {
	                  if (isOrderForRecovery) {
	                    setOfrReceiptStatus(null);
	                    setOfrTruthAcknowledged(false);
	                    setIsOfrAssessmentOpen(true);
	                    return;
	                  }
	                  setIsPaymentModalOpen(true);
	                }}
	              />
	              <DecisionCard 
	                title="Transfer" 
	                icon={<Briefcase size={20} />} 
	                description="Assign to hirer/driver"
	                variant="primary"
	                isActive={ticket.decision === 'transfer'}
	                onClick={() => setIsLiabilityModalOpen(true)}
	              />
	              <DecisionCard
	                title={(() => {
	                  return isOrderForRecovery ? 'Statutory Declaration' : 'Appeal';
	                })()}
	                icon={<Scale size={20} />}
	                description={(() => {
	                  return isOrderForRecovery ? 'Non-receipt pathway (OFR)' : 'Challenge validity';
	                })()}
	                isActive={ticket.decision === 'appeal' || statusKey.startsWith('appeal_') || statusKey === 'appeal_pending'}
	                disabled={!isOrderForRecovery && !canAppeal}
	                disabledTooltip={!isOrderForRecovery ? undefined : appealBlockedReason}
	                onClick={() => {
	                   if (isOrderForRecovery) {
	                     setOfrReceiptStatus('not_received');
	                     setOfrTruthAcknowledged(false);
	                     setIsOfrAssessmentOpen(true);
	                     return;
	                   }
	                   setAppealText('');
	                   setAppealReason('Loading');
	                   setIsAppealModalOpen(true);
	                }}
	              />
	            </div>
	          </section>

          {/* Key Details Card */}
          <div className="bg-white dark:bg-slate-900/80 p-5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm backdrop-blur-sm">
            <h3 className="font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
              <AlertOctagon size={18} className="text-primary-600 dark:text-primary-400" /> Contravention Data
            </h3>
            <div className="space-y-4">
               <div className="flex justify-between border-b border-slate-50 dark:border-slate-800 pb-2">
                 <span className="text-slate-500 dark:text-slate-400 text-sm flex items-center gap-2"><Clock size={14}/> Date & Time</span>
                 <span className="font-medium text-slate-800 dark:text-slate-200">{new Date(ticket.contravention_date || ticket.issue_date).toLocaleString()}</span>
               </div>
               <div className="flex justify-between border-b border-slate-50 dark:border-slate-800 pb-2">
                 <span className="text-slate-500 dark:text-slate-400 text-sm flex items-center gap-2"><MapPin size={14}/> Location</span>
                 <span className="font-medium text-slate-800 dark:text-slate-200 text-right">{ticket.location}</span>
               </div>
               <div className="flex justify-between border-b border-slate-50 dark:border-slate-800 pb-2">
                 <span className="text-slate-500 dark:text-slate-400 text-sm flex items-center gap-2"><Flag size={14}/> Type</span>
                 <span className="font-medium text-slate-800 dark:text-slate-200 text-right">{ticket.contravention_type || 'Unknown'}</span>
               </div>
               <div className="flex justify-between pt-1">
                 <span className="text-slate-500 dark:text-slate-400 text-sm flex items-center gap-2"><User size={14}/> Assigned Driver</span>
                 <div className="flex items-center gap-2">
                   <span className={`font-medium ${ticket.assigned_driver_name ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {ticket.assigned_driver_name || 'Unassigned'}
                   </span>
                   {/* Auto-assigned badge */}
                   {ticket.auto_assigned && ticket.audit_result && (
                     <span
                       className="text-[10px] font-bold px-2 py-0.5 rounded border bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800 cursor-help"
                       title={`Auto-assigned with ${ticket.audit_result.confidenceScore}% confidence. ${ticket.audit_result.reason || ''}`}
                     >
                       Auto ({ticket.audit_result.confidenceScore}%)
                     </span>
                   )}
                   {/* No hire confirmed badge */}
                   {ticket.no_hire_confirmed && (
                     <span
                       className="text-[10px] font-bold px-2 py-0.5 rounded border bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800 cursor-help"
                       title={`No hire confirmed: ${ticket.no_hire_confirmation?.reason || 'Confirmed by user'}`}
                     >
                       No Hire ✓
                     </span>
                   )}
                   {!ticket.assigned_driver_name && !ticket.no_hire_confirmed && (
                     <button
                       onClick={handleOpenAssignModal}
                       className="text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 px-2 py-0.5 rounded border border-primary-100 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/40 font-bold"
                     >
                       Assign
                     </button>
                   )}
                   {/* Override button for auto-assigned tickets */}
                   {ticket.auto_assigned && ticket.assigned_driver_name && (
                     <button
                       onClick={handleOpenAssignModal}
                       className="text-[10px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 underline"
                     >
                       Override
                     </button>
                   )}
                 </div>
               </div>
               {/* Blocking factors explanation */}
               {ticket.audit_result && !ticket.auto_assigned && ticket.audit_result.blockingFactors?.length > 0 && !ticket.no_hire_confirmed && (
                 <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-700 dark:text-amber-300">
                   <span className="font-bold">Why not auto-assigned: </span>
                   {ticket.audit_result.blockingFactors.join('; ')}
                 </div>
               )}
                {(ticket.audit_result?.warningFactors?.length ?? 0) > 0 && !ticket.no_hire_confirmed && (
                  <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300">
                    <span className="font-bold">Auto-assigned with warnings: </span>
                    {(ticket.audit_result?.warningFactors ?? []).join('; ')}
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Council Payment Modal */}
      <Modal 
        isOpen={isPaymentModalOpen} 
        onClose={() => {
          setIsPaymentModalOpen(false);
          setPaymentStep('details');
        }} 
        title="Pay Council"
        maxWidth="max-w-md"
      >
        {/* ... existing modal content ... */}
        <div className="p-6">
          {paymentStep === 'details' ? (
            <div className="space-y-4">
               <div className="flex items-center gap-3 mb-6 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-900">
                 <div className="bg-white dark:bg-slate-900 p-2 rounded-full shadow-sm">
                    <Globe className="text-blue-600 dark:text-blue-400" size={20} />
                 </div>
                 <div>
                    <p className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase">Payment Portal</p>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{ticket.issuing_authority}</p>
                 </div>
               </div>
               
               <div className="pt-4 space-y-3">
                 <a 
                   href={CouncilPaymentService.getPaymentLink(ticket.issuing_authority, ticket.pcn_number, ticket.vrn)}
                   target="_blank"
                   rel="noreferrer"
                   className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                   onClick={() => setPaymentStep('confirm')}
                 >
                   Open Payment Portal <ExternalLink size={16} />
                 </a>
                 <button onClick={() => setIsPaymentModalOpen(false)} className="w-full py-2.5 text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800 rounded-lg text-sm font-medium">
                   Cancel
                 </button>
               </div>
            </div>
          ) : (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
               <h3 className="font-bold text-slate-800 dark:text-white">Confirm Payment</h3>
               <p className="text-sm text-slate-600 dark:text-slate-400">
                 Once you have completed the payment on the council website, verify it here to update your fleet records.
               </p>
               
               <div>
                 <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Receipt / Transaction Ref</label>
                 <input 
                   type="text"
                   placeholder="e.g. TXN-12345678"
                   value={transactionRef}
                   onChange={(e) => setTransactionRef(e.target.value)}
                   className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 dark:text-white"
                 />
               </div>

               <div className="pt-2 flex gap-3">
                  <button 
                    onClick={() => setPaymentStep('details')}
                    className="flex-1 py-2 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium"
                  >
                    Back
                  </button>
                  <button
                    disabled={recordingPayment}
                    onClick={handleConfirmPayment}
                    className="flex-1 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg font-bold flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {recordingPayment ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Mark as Paid
                  </button>
               </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Order for Recovery: Statutory Declaration Assessment */}
      <Modal
        isOpen={isOfrAssessmentOpen}
        onClose={() => setIsOfrAssessmentOpen(false)}
        title="Order for Recovery: Choose your route"
        maxWidth="max-w-xl"
      >
        <div className="p-6 space-y-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 mt-0.5" />
              <div className="text-sm text-amber-900 dark:text-amber-100">
                <div className="font-bold">Order for Recovery is court enforcement.</div>
                <div className="mt-1 text-amber-800 dark:text-amber-200">
                  Before paying, confirm whether you genuinely received the earlier statutory notices. If you did not, you may be able to file a witness
                  statement/statutory declaration to reset the case.
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setOfrReceiptStatus('received')}
              className={`p-4 rounded-xl border text-left transition-colors ${
                ofrReceiptStatus === 'received'
                  ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              }`}
            >
              <div className="font-bold text-slate-900 dark:text-white">I received the earlier notices</div>
              <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Proceed to payment to stop enforcement and added costs.
              </div>
            </button>

            <button
              type="button"
              onClick={() => setOfrReceiptStatus('not_received')}
              className={`p-4 rounded-xl border text-left transition-colors ${
                ofrReceiptStatus === 'not_received'
                  ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              }`}
            >
              <div className="font-bold text-slate-900 dark:text-white">I did NOT receive earlier notices</div>
              <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Review statutory declaration / witness statement options (non-receipt).
              </div>
            </button>
          </div>

          {ofrReceiptStatus === 'not_received' && ticket && (
            <div className="space-y-3">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4">
                <div className="text-sm font-bold text-slate-900 dark:text-white">Next steps</div>
                <ul className="mt-2 text-sm text-slate-600 dark:text-slate-300 space-y-2 list-disc pl-5">
                  {isStatutoryEnforcement ? (
                    <>
                      <li>
                        Case type: {tecForms ? tecForms.categoryLabel : 'Traffic Enforcement Centre (TEC) order'}.{' '}
                        {tecForms?.matchedKeywords.length
                          ? `Matched keywords: ${tecForms.matchedKeywords.join(', ')}.`
                          : 'Verify the form code on the Order for Recovery if unsure.'}
                      </li>
                      <li>
                        Recommended forms: {tecForms ? tecForms.primaryForm : 'Statutory declaration'}.{' '}
                        {tecForms
                          ? tecForms.isOutOfTime
                            ? `Out of time: also submit ${tecForms.outOfTimeForm}.`
                            : `If you are out of time (>21 days), also submit ${tecForms.outOfTimeForm}.`
                          : ''}
                      </li>
                      <li>Only use this route if the statement is true (non-receipt of the Notice to Owner and/or Charge Certificate).</li>
                      <li>
                        Official forms:{' '}
                        {tecForms ? (
                          <>
                            <a
                              href={tecForms.primaryLink}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                            >
                              {tecForms.primaryForm}
                            </a>
                            {tecForms.outOfTimeLink && (
                              <>
                                {' '}·{' '}
                                <a
                                  href={tecForms.outOfTimeLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                                >
                                  {tecForms.outOfTimeForm}
                                </a>
                              </>
                            )}
                            {tecForms.guidanceLink && (
                              <>
                                {' '}·{' '}
                                <a
                                  href={tecForms.guidanceLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                                >
                                  Guidance notes
                                </a>
                              </>
                            )}
                          </>
                        ) : (
                          <a
                            href="https://www.gov.uk/government/collections/traffic-enforcement-centre-forms"
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                          >
                            Traffic Enforcement Centre (TEC) forms
                          </a>
                        )}
                      </li>
                      <li>
                        Full collection:{' '}
                        <a
                          href="https://www.gov.uk/government/collections/traffic-enforcement-centre-forms"
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                        >
                          TEC forms list
                        </a>
                        .
                      </li>
                    </>
                  ) : (
                    <>
                      <li>Order for Recovery is typically a council/TfL process; confirm the enforcement type on the document.</li>
                      <li>If this is the first notice you’ve seen, you may still have a statutory declaration/witness statement route (depends on issuer).</li>
                    </>
                  )}
                </ul>
              </div>

              {isStatutoryEnforcement && tecForms && (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4">
                  <div className="text-sm font-bold text-slate-900 dark:text-white">Official forms</div>

                  {/* TE Forms - Use new wizard */}
                  {tecForms.category === 'traffic_enforcement_order' && (
                    <>
                      <div className="mt-1 text-xs text-slate-700 dark:text-slate-300">
                        Complete the official TE9 Witness Statement form using our guided wizard. All fields will be prefilled from the ticket data.
                        {tecForms.isOutOfTime && ' Since this is out of time, TE7 (Out of Time Application) will also be included.'}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setIsOfrAssessmentOpen(false);
                          setIsTeFormWizardOpen(true);
                        }}
                        disabled={!ofrTruthAcknowledged}
                        className="mt-3 w-full px-4 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        <FileText size={18} />
                        Fill {tecForms.primaryForm}{tecForms.isOutOfTime ? ` + ${tecForms.outOfTimeForm}` : ''} Form
                      </button>
                      <div className="mt-2 text-xs text-slate-700 dark:text-slate-300 text-center">
                        Step-by-step form filling with live preview and automatic PDF generation
                      </div>
                    </>
                  )}

                  {/* PE Forms - Keep existing download flow */}
                  {tecForms.category === 'penalty_charge_notice' && (
                    <>
                      <div className="mt-1 text-xs text-slate-700 dark:text-slate-300">
                        Download the official TEC forms for submission. We prefill all available fields; review and edit before signing. PE forms require a solicitor signature before submission.
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleDownloadTecForm('primary')}
                          disabled={downloadingForm === 'primary'}
                          className="px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                          {downloadingForm === 'primary' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                          Download {tecForms.primaryForm} (prefilled)
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadTecForm('outOfTime')}
                          disabled={downloadingForm === 'outOfTime'}
                          className="px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                          {downloadingForm === 'outOfTime' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                          Download {tecForms.outOfTimeForm} (prefilled)
                        </button>
                      </div>
                      <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        PE2/PE3 must be signed by a solicitor. Download, sign, and reupload the signed PDFs before emailing TEC.
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <label className="space-y-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Full name</span>
                          <input
                            value={formPrefill.fullName}
                            onChange={(e) => updatePrefillValue('fullName', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Company (optional)</span>
                          <input
                            value={formPrefill.company}
                            onChange={(e) => updatePrefillValue('company', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Address line 1</span>
                          <input
                            value={formPrefill.addressLine1}
                            onChange={(e) => updatePrefillValue('addressLine1', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Address line 2 (optional)</span>
                          <input
                            value={formPrefill.addressLine2}
                            onChange={(e) => updatePrefillValue('addressLine2', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">City</span>
                          <input
                            value={formPrefill.city}
                            onChange={(e) => updatePrefillValue('city', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Postcode</span>
                          <input
                            value={formPrefill.postcode}
                            onChange={(e) => updatePrefillValue('postcode', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Email</span>
                          <input
                            value={formPrefill.email}
                            onChange={(e) => updatePrefillValue('email', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Phone</span>
                          <input
                            value={formPrefill.phone}
                            onChange={(e) => updatePrefillValue('phone', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                          />
                        </label>
                        <div className="sm:col-span-2 text-xs text-slate-700 dark:text-slate-300">
                          Email and phone populate the TEC form contact section and the reply‑to details in the TEC email. Clear them if you don't want them included.
                        </div>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Vehicle reg (VRM)</span>
                          <input
                            value={formPrefill.vrm}
                            onChange={(e) => updatePrefillValue('vrm', e.target.value.toUpperCase())}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">PCN number</span>
                          <input
                            value={formPrefill.pcnNumber}
                            onChange={(e) => updatePrefillValue('pcnNumber', e.target.value)}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                          />
                        </label>
                      </div>
                      <div className="mt-3">
                        <label className="space-y-1 block">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                            Reason / explanation (editable)
                          </span>
                          <textarea
                            value={formPrefill.reason}
                            onChange={(e) => updatePrefillValue('reason', e.target.value)}
                            className="w-full min-h-[90px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
                            placeholder="Explain why the declaration is being filed (e.g., non-receipt of Notice to Owner / Charge Certificate)."
                          />
                          <div className="mt-1 text-xs text-slate-700 dark:text-slate-300">
                            This text will populate the form's reason/statement section. Review before signing.
                          </div>
                        </label>
                      </div>
                    </>
                  )}

                  {tecForms.guidanceLink && (
                    <div className="mt-3">
                      <a
                        href={tecForms.guidanceLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300"
                      >
                        <ExternalLink size={14} /> Guidance notes
                      </a>
                    </div>
                  )}
                </div>
              )}

              <label className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded-lg p-4">
                <input
                  type="checkbox"
                  checked={ofrTruthAcknowledged}
                  onChange={(e) => setOfrTruthAcknowledged(e.target.checked)}
                  className="mt-1"
                />
                <div className="text-sm text-red-800 dark:text-red-200">
                  <div className="font-bold">Important legal warning</div>
                  <div className="mt-1">
                    A statutory declaration / witness statement must be truthful. Making a false declaration can be a criminal offence (perjury). Only proceed
                    if you genuinely did not receive the notices.
                  </div>
                </div>
              </label>
            </div>
          )}

          <div className="pt-2 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => setIsOfrAssessmentOpen(false)}
              className="sm:flex-1 py-2.5 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium"
            >
              Close
            </button>

            {ofrReceiptStatus === 'received' && (
              <button
                type="button"
                onClick={() => {
                  setIsOfrAssessmentOpen(false);
                  setIsPaymentModalOpen(true);
                }}
                className="sm:flex-1 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold"
              >
                Proceed to Payment
              </button>
            )}

            {ofrReceiptStatus === 'not_received' && (
              <>
                {tecForms && tecForms.category === 'traffic_enforcement_order' && (
                  <button
                    type="button"
                    onClick={() => setIsTeFormWizardOpen(true)}
                    disabled={!ofrTruthAcknowledged}
                    className="sm:flex-1 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold disabled:opacity-60"
                  >
                    Fill TE Form
                  </button>
                )}
                {tecForms && tecForms.category === 'penalty_charge_notice' && (
                  <button
                    type="button"
                    onClick={() => setIsTecSubmitOpen(true)}
                    disabled={!ofrTruthAcknowledged}
                    className="sm:flex-1 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold disabled:opacity-60"
                  >
                    Send to TEC
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleMarkStatDecPath}
                  disabled={!ofrTruthAcknowledged}
                  className="sm:flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold disabled:opacity-60"
                >
                  Mark Statutory Declaration Route
                </button>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* TEC Submission Modal */}
      <Modal
        isOpen={isTecSubmitOpen}
        onClose={handleTecCloseRequest}
        title="Send statutory declaration to TEC"
        maxWidth="max-w-2xl"
      >
        <div className="p-6 space-y-4">
          {/* Progress indicator - shows completion status */}
          <div className="flex items-center gap-2 text-sm pb-2 border-b border-slate-200 dark:border-slate-700">
            <div className={`flex items-center gap-1.5 ${ofrTruthAcknowledged ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'}`}>
              {ofrTruthAcknowledged ? <CheckCircle2 size={16} /> : <div className="w-4 h-4 rounded-full border-2 border-current" />}
              <span className="text-xs font-medium">Legal</span>
            </div>
            <div className="flex-1 h-0.5 bg-slate-200 dark:bg-slate-700" />
            <div className={`flex items-center gap-1.5 ${
              tecForms?.category !== 'penalty_charge_notice' || tecAttachmentFiles.length > 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-slate-400 dark:text-slate-500'
            }`}>
              {(tecForms?.category !== 'penalty_charge_notice' || tecAttachmentFiles.length > 0)
                ? <CheckCircle2 size={16} />
                : <div className="w-4 h-4 rounded-full border-2 border-current" />
              }
              <span className="text-xs font-medium">Attachments</span>
            </div>
            <div className="flex-1 h-0.5 bg-slate-200 dark:bg-slate-700" />
            <div className={`flex items-center gap-1.5 ${
              (isPeChecklistComplete || tecForms?.category !== 'penalty_charge_notice') && ofrTruthAcknowledged && (tecForms?.category !== 'penalty_charge_notice' || tecAttachmentFiles.length > 0) && isReasonConfirmed
                ? 'text-green-600 dark:text-green-400'
                : 'text-slate-400 dark:text-slate-500'
            }`}>
              {((isPeChecklistComplete || tecForms?.category !== 'penalty_charge_notice') && ofrTruthAcknowledged && (tecForms?.category !== 'penalty_charge_notice' || tecAttachmentFiles.length > 0) && isReasonConfirmed)
                ? <CheckCircle2 size={16} />
                : <div className="w-4 h-4 rounded-full border-2 border-current" />
              }
              <span className="text-xs font-medium">Ready</span>
            </div>
          </div>

          {tecCloseWarning && (
            <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-lg p-3 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                You have uploaded signed PDFs but haven't completed the checklist. Close anyway?
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setTecCloseWarning(false)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-amber-200"
                >
                  Keep editing
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTecCloseWarning(false);
                    setIsTecSubmitOpen(false);
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 text-white"
                >
                  Close anyway
                </button>
              </div>
            </div>
          )}
          <div className="text-sm text-slate-600 dark:text-slate-300">
            This will send the official TEC forms by email. You are responsible for confirming all statements are true.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="space-y-1 text-sm">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">To</span>
              <input
                value={tecEmailTo}
                onChange={(e) => setTecEmailTo(e.target.value)}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Reply-to</span>
              <input
                value={tecReplyTo}
                onChange={(e) => {
                  setTecReplyTouched(true);
                  setTecReplyTo(e.target.value);
                }}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2"
              />
            </label>
          </div>

          <label className="space-y-1 text-sm">
            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Subject</span>
            <input
              value={tecEmailSubject}
              onChange={(e) => {
                setTecSubjectTouched(true);
                setTecEmailSubject(e.target.value);
              }}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2"
            />
          </label>

          <label className="space-y-1 text-sm">
            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Statement (optional)</span>
            <textarea
              value={tecStatement}
              onChange={(e) => {
                setTecStatementTouched(true);
                setTecStatement(e.target.value);
              }}
              className="w-full min-h-[120px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2"
              placeholder="Add any context you want TEC to see."
            />
          </label>
          <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 p-4 text-sm space-y-2">
            <div className="text-xs font-semibold text-slate-600 dark:text-slate-400">Reason on the form (required)</div>
            <textarea
              value={formPrefill.reason}
              onChange={(e) => updatePrefillValue('reason', e.target.value)}
              className="w-full min-h-[90px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
              placeholder="Explain why the declaration is being filed."
            />
            <label className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={tecReasonConfirmed}
                onChange={(e) => setTecReasonConfirmed(e.target.checked)}
                className="mt-0.5"
              />
              I confirm this is the correct reason for the statutory declaration / witness statement.
            </label>
          </div>

          {tecForms && (
            <div className="bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-lg p-4 text-sm space-y-3">
              <div className="font-semibold text-slate-800 dark:text-slate-200">Attachments</div>
              {tecForms.category === 'penalty_charge_notice' ? (
                <>
                  <div className="text-slate-600 dark:text-slate-400">
                    Upload the signed PE2/PE3 PDFs before sending (solicitor signature required).
                  </div>
                  <input
                    key={tecFileInputKey}
                    type="file"
                    accept=".pdf,application/pdf"
                    multiple
                    onChange={(e) => handleTecFileChange(e.target.files)}
                    className="text-sm"
                  />
                  {tecAttachmentFiles.length > 0 && (
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      Attached: {tecAttachmentFiles.map((file) => file.name).join(', ')}
                    </div>
                  )}
                  {tecAttachmentFiles.length > 0 && (
                    <button
                      type="button"
                      onClick={handleReplaceTecAttachments}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      Replace uploaded PDFs
                    </button>
                  )}
                  <div className="pt-2 space-y-2 text-xs text-slate-600 dark:text-slate-300">
                    <div className="font-semibold text-slate-700 dark:text-slate-200">Checklist</div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={tecPeChecklist.signed}
                        onChange={(e) => setTecPeChecklist((prev) => ({ ...prev, signed: e.target.checked }))}
                      />
                      Signed by a solicitor
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={tecPeChecklist.scanned}
                        onChange={(e) => setTecPeChecklist((prev) => ({ ...prev, scanned: e.target.checked }))}
                      />
                      Scanned as PDF
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={tecPeChecklist.legible}
                        onChange={(e) => setTecPeChecklist((prev) => ({ ...prev, legible: e.target.checked }))}
                      />
                      Legible and complete
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-slate-600 dark:text-slate-400">
                    {tecForms.primaryForm} will be attached automatically.
                    {tecIncludeOutOfTime ? ` ${tecForms.outOfTimeForm} will also be attached.` : ''}
                  </div>
                  {!tecForms.isOutOfTime && (
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                      <input
                        type="checkbox"
                        checked={tecIncludeOutOfTime}
                        onChange={(e) => setTecIncludeOutOfTime(e.target.checked)}
                      />
                      Include {tecForms.outOfTimeForm}
                    </label>
                  )}
                </>
              )}
            </div>
          )}

          {/* Legal acknowledgment - show in TEC modal so users don't need OFR Assessment first */}
          {!ofrTruthAcknowledged && (
            <label className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded-lg p-4">
              <input
                type="checkbox"
                checked={ofrTruthAcknowledged}
                onChange={(e) => setOfrTruthAcknowledged(e.target.checked)}
                className="mt-1"
              />
              <div className="text-sm text-red-800 dark:text-red-200">
                <div className="font-bold">Important legal warning</div>
                <div className="mt-1">
                  A statutory declaration / witness statement must be truthful. Making a false declaration can be a criminal offence (perjury). Only proceed if you genuinely did not receive the notices.
                </div>
              </div>
            </label>
          )}

          {tecError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {tecError}
            </div>
          )}

          {/* Show missing requirements feedback */}
          {!tecSending && (
            <div className="space-y-1">
              {!ofrTruthAcknowledged && (
                <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertTriangle size={12} /> Please acknowledge the legal warning above
                </div>
              )}
              {tecForms?.category === 'penalty_charge_notice' && !tecAttachmentFiles.length && (
                <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertTriangle size={12} /> Upload signed PE2/PE3 PDFs
                </div>
              )}
              {tecForms?.category === 'penalty_charge_notice' && tecAttachmentFiles.length > 0 && !isPeChecklistComplete && (
                <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertTriangle size={12} /> Complete the PE form checklist above
                </div>
              )}
              {!reasonValue && (
                <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertTriangle size={12} /> Add a reason before sending to TEC
                </div>
              )}
              {reasonValue && !tecReasonConfirmed && (
                <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertTriangle size={12} /> Confirm the reason for this submission
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={handleTecCloseRequest}
              className="sm:flex-1 py-2.5 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSendTecEmail}
              disabled={tecSending || !ofrTruthAcknowledged || !isReasonConfirmed || (tecForms?.category === 'penalty_charge_notice' && (!tecAttachmentFiles.length || !isPeChecklistComplete))}
              className="sm:flex-1 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {tecSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Send to TEC
            </button>
          </div>
        </div>
      </Modal>

      {/* Appeal Modal */}
      <Modal
        isOpen={isAppealModalOpen}
        onClose={() => {
          setIsAppealModalOpen(false);
          setAppealStrategy(null); // Clear strategy when closing modal
        }}
        title="Draft Appeal"
        maxWidth="max-w-lg"
      >
        {/* ... existing modal content ... */}
        <div className="p-6 space-y-4">
          {/* AI Strategy Recommendation Button */}
          {!appealStrategy && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <p className="text-sm text-purple-900 dark:text-purple-100 mb-3">
                <strong>ðŸ’¡ Get AI-powered insights</strong> before appealing! See your success probability, required evidence, and legal grounds.
              </p>
              <button
                onClick={handleGetAppealStrategy}
                disabled={loadingStrategy}
                className={`w-full px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  hasAiAppeals
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                }`}
              >
                {loadingStrategy ? <Loader2 size={16} className="animate-spin" /> : hasAiAppeals ? <Sparkles size={16} /> : <Lock size={16} />}
                {hasAiAppeals ? (loadingStrategy ? 'Analyzing...' : 'Get AI Recommendation') : 'AI Recommendation (Upgrade to Pro)'}
              </button>
            </div>
          )}

          {/* Display Appeal Strategy Card */}
          {appealStrategy && <AppealStrategyCard strategy={appealStrategy} />}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Appeal Strategy</label>
            <select
              value={appealReason}
              onChange={(e) => setAppealReason(e.target.value)}
              className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm bg-white dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none mb-2"
            >
              <option value="Loading">Loading / Unloading (Exempt)</option>
              <option value="Signage">Signage Issues</option>
              <option value="Procedural">Procedural Error (Late Service)</option>
              <option value="Stolen">Vehicle Stolen</option>
              <option value="Cloned">Cloned Vehicle (Not Ours)</option>
              <option value="Hire">Hire Company (Transfer Liability)</option>
            </select>
            <button
              onClick={handleGenerateAppeal}
              disabled={isGenerating}
              className={`w-full px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                hasAiAppeals
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
              }`}
            >
              {isGenerating ? <Loader2 size={16} className="animate-spin" /> : hasAiAppeals ? <Sparkles size={16} /> : <Lock size={16} />}
              {hasAiAppeals ? 'Generate AI Draft' : 'AI Draft (Upgrade to Pro)'}
            </button>

            {appealText && (
              <>
                <button
                  onClick={handleCopyAllFields}
                  className="w-full px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors bg-slate-600 text-white hover:bg-slate-700"
                  title="Copy all appeal information to clipboard in formatted text"
                >
                  <Copy size={16} />
                  Copy All Fields
                </button>
                <button
                  onClick={() => {
                    setIsAppealModalOpen(false);
                    handleOpenSubmissionWizard('appeal');
                  }}
                  className="w-full px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Send size={16} />
                  Submit Appeal via Portal
                </button>
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Representation Letter</label>
            <textarea 
              value={appealText}
              onChange={(e) => setAppealText(e.target.value)}
              className="w-full h-48 p-3 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none resize-none font-sans leading-relaxed bg-white dark:bg-slate-800 dark:text-white"
              placeholder="Your appeal text will appear here..."
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-2">
             <button onClick={() => setIsAppealModalOpen(false)} className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm">Cancel</button>
             <button
               onClick={handleSendAppeal}
               disabled={submittingAppeal}
               className="px-4 py-2 bg-slate-900 dark:bg-white dark:text-slate-900 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-70"
             >
               {submittingAppeal ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Submit Appeal
             </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!appealOutcomeModal?.open}
        onClose={() => setAppealOutcomeModal(null)}
        title="Confirm appeal outcome"
        maxWidth="max-w-md"
      >
        <div className="p-6">
          <p className="text-slate-700 dark:text-slate-300 text-sm">
            Mark this appeal as{' '}
            <span className="font-bold">
              {appealOutcomeModal?.outcome === 'appeal_won' ? 'Won' : 'Lost'}
            </span>
            ?
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setAppealOutcomeModal(null)}
              className="px-4 py-2 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                const outcome = appealOutcomeModal?.outcome;
                if (outcome) void handleMarkAppealOutcome(outcome);
              }}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold transition-colors"
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      {/* Transfer Liability Modal */}
      <Modal
        isOpen={isLiabilityModalOpen}
        onClose={() => {
          setIsLiabilityModalOpen(false);
          setPackUrl(null);
          setGenerationSteps([]);
          setTransferStep('check'); // Reset
          setSubmissionProofFile(null);
          setTransferProofPath(null);
          setTransferSubmittedAt(null);
          setTransferReferenceNumber('');
          setDetectedTransferAuthority(null);
        }}
        title={
          transferStep === 'helper' ? "Submission Helper" :
          transferStep === 'proof' ? "Confirm Submission" :
          "Transfer of Liability Checker"
        }
        maxWidth="max-w-xl"
      >
        {/* ... existing modal content ... */}
        <div className="p-6">
          
          {/* Step 1: Gatekeeper (Bus Lanes) */}
          {isTransferBlocked ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 rounded-lg p-6 text-center">
              <AlertOctagon className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <h3 className="font-bold text-red-800 dark:text-red-400 text-lg">Transfer Not Available</h3>
              <p className="text-red-700 dark:text-red-300 text-sm mt-2">
                This appears to be a London Bus Lane contravention. 
                <br/><strong>Liability typically cannot be transferred</strong> for these types of tickets. The registered keeper (you) is usually liable.
              </p>
              <button onClick={() => { setIsLiabilityModalOpen(false); setIsPaymentModalOpen(true); }} className="mt-4 px-4 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700">
                Go to Payment
              </button>
            </div>
          ) : transferStep === 'done' ? (
            <div className="space-y-6 animate-in fade-in">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-5 flex gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5" />
                <div>
                  <h4 className="font-bold text-green-900 dark:text-green-100">Transfer Submitted Successfully</h4>
                  <p className="text-sm text-green-800 dark:text-green-200 mt-1">
                    Ticket status updated to <span className="font-bold">transferred</span> and submission recorded.
                  </p>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4 text-sm space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-500 dark:text-slate-400">Authority</span>
                  <span className="font-medium text-slate-900 dark:text-slate-200 truncate">
                    {detectedTransferAuthority?.name || ticket.issuing_authority}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-500 dark:text-slate-400">Proof file</span>
                  <span className="font-medium text-slate-900 dark:text-slate-200 truncate">{submissionProofFile?.name}</span>
                </div>
                {transferReferenceNumber && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500 dark:text-slate-400">Reference</span>
                    <span className="font-mono text-sm font-semibold text-slate-900 dark:text-slate-200">
                      {transferReferenceNumber}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-500 dark:text-slate-400">Submitted</span>
                  <span className="font-medium text-slate-900 dark:text-slate-200">
                    {transferSubmittedAt ? new Date(transferSubmittedAt).toLocaleString() : '-'}
                  </span>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  ✓ Submission tracked in database<br/>
                  ✓ View in <span className="font-bold">Submissions</span> tab
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setIsLiabilityModalOpen(false);
                    setPackUrl(null);
                    setGenerationSteps([]);
                    setTransferStep('check');
                    setSubmissionProofFile(null);
                    setTransferProofPath(null);
                    setTransferSubmittedAt(null);
                    setTransferReferenceNumber('');
                    setDetectedTransferAuthority(null);
                    // Switch to submissions tab to show the new submission
                    setActiveEvidenceTab('submissions');
                  }}
                  className="px-4 py-2 bg-slate-900 dark:bg-white dark:text-slate-900 text-white rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200"
                >
                  View Submission
                </button>
              </div>
            </div>
          ) : transferStep === 'helper' ? (
            /* SUBMISSION HELPER (SIDECAR) */
            <div className="animate-in slide-in-from-right-4">
               {/* Deadline Countdown */}
               {appealDeadlineDate && (
                 <DeadlineCountdown deadlineDate={appealDeadlineDate.toISOString()} />
               )}

               {/* Portal URL Card */}
               {portalUrl ? (
                 <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 mb-4">
                   <div className="flex items-start gap-3">
                     <Globe className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                     <div className="flex-1">
                       <h4 className="text-sm font-bold text-primary-900 dark:text-primary-100 mb-1">
                         Submission Portal
                       </h4>
                       <p className="text-xs text-primary-700 dark:text-primary-300 mb-2">
                         Click below to open the {authorityData?.name || ticket.issuing_authority} portal:
                       </p>
                       <a
                         href={portalUrl}
                         target="_blank"
                         rel="noopener noreferrer"
                         className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded text-sm font-medium transition-colors"
                       >
                         <ExternalLink className="w-4 h-4" />
                         Open Portal
                       </a>
                     </div>
                   </div>
                 </div>
               ) : ticket?.issuing_authority ? (
                 <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
                   <div className="flex items-start gap-3">
                     <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                     <div className="flex-1">
                       <p className="text-xs text-amber-700 dark:text-amber-300">
                         Portal URL not available for {ticket.issuing_authority}. Most councils and private parking companies accept portal submissions only.
                       </p>
                     </div>
                   </div>
                 </div>
               ) : null}

               <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900 p-4 rounded-lg flex gap-3 mb-6">
                 <Clipboard className="w-6 h-6 text-amber-600 dark:text-amber-400 mt-0.5" />
                 <div>
                   <h4 className="font-bold text-amber-900 dark:text-amber-100">Submission Sidecar</h4>
                   <p className="text-sm text-amber-800 dark:text-amber-200">
                     Keep this window open. Use the copy buttons below to quickly fill the form on the Council Portal.
                   </p>
                 </div>
               </div>

               {/* Council-specific field requirements */}
               {(() => {
                 const councilReqs = getCouncilRequirements(ticket.issuing_authority);
                 return (
                   <>
                     {councilReqs && councilReqs.notes && (
                       <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 rounded-lg mb-4">
                         <p className="text-xs text-blue-700 dark:text-blue-300">
                           <span className="font-semibold">{councilReqs.authorityName}:</span> {councilReqs.notes}
                         </p>
                       </div>
                     )}
                     <div className="grid grid-cols-2 gap-4 mb-4">
                       <CopyField
                         label="PCN Number"
                         value={ticket.pcn_number}
                         required={isFieldRequired(ticket.issuing_authority, 'pcn_number')}
                       />
                       <CopyField
                         label="Vehicle VRM"
                         value={ticket.vrn}
                         required={isFieldRequired(ticket.issuing_authority, 'vrm')}
                       />
                       <CopyField
                         label="Hirer Name"
                         value={linkedHire?.driver_name || ''}
                         required={isFieldRequired(ticket.issuing_authority, 'hirer_name')}
                       />
                       <CopyField
                         label="Hirer Address"
                         value={
                           linkedHire?.hirer_address ||
                           (linkedDriver?.address_line1 && linkedDriver?.postcode
                             ? `${linkedDriver.address_line1}, ${linkedDriver.postcode}`
                             : 'See Agreement')
                         }
                         required={isFieldRequired(ticket.issuing_authority, 'hirer_address')}
                       />
                     </div>
                   </>
                 );
               })()}

               <div className="mb-4">
                 <div className="flex flex-col gap-2 mb-1">
                   <div className="flex justify-between items-center">
                     <span className="text-xs text-slate-500 font-semibold">Representation Text (Editable)</span>
                     <div className="flex items-center gap-2">
                       <span className="text-[10px] text-slate-400 font-semibold">Style:</span>
                       <button
                         onClick={() => {
                           setTemplateVariant('formal');
                           setRepresentationText(generateRepresentationText('formal'));
                         }}
                         className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${
                           templateVariant === 'formal'
                             ? 'bg-primary-600 text-white'
                             : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                         }`}
                       >
                         Formal
                       </button>
                       <button
                         onClick={() => {
                           setTemplateVariant('concise');
                           setRepresentationText(generateRepresentationText('concise'));
                         }}
                         className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${
                           templateVariant === 'concise'
                             ? 'bg-primary-600 text-white'
                             : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                         }`}
                       >
                         Concise
                       </button>
                       <button
                         onClick={() => {
                           setTemplateVariant('detailed');
                           setRepresentationText(generateRepresentationText('detailed'));
                         }}
                         className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${
                           templateVariant === 'detailed'
                             ? 'bg-primary-600 text-white'
                             : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                         }`}
                       >
                         Detailed
                       </button>
                     </div>
                   </div>
                   <div className="flex justify-between items-center">
                     <span className="text-[10px] text-slate-400">{getTemplateDescription(templateVariant)}</span>
                     <div className="flex items-center gap-3">
                       <span className={`text-xs font-mono ${representationText.length > 2000 ? "text-red-500 font-bold" : "text-slate-400"}`}>
                         {representationText.length} / 2000 chars
                       </span>
                       <button
                         onClick={() => {
                           navigator.clipboard.writeText(representationText);
                           addToast("Representation text copied", "success");
                         }}
                         className="inline-flex items-center gap-1 text-xs text-primary-600 font-bold hover:underline"
                       >
                         <Copy size={12} /> Copy All
                       </button>
                     </div>
                   </div>
                 </div>
                 <textarea
                   value={representationText}
                   onChange={(e) => setRepresentationText(e.target.value)}
                   className="w-full h-32 p-3 border border-slate-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                   placeholder="Edit your representation text here..."
                 />
                 {representationText.length > 2000 && (
                   <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                     <AlertTriangle size={12} /> Warning: Some councils limit text to 2000 characters
                   </p>
                 )}
               </div>

               <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800 mb-6">
                 <h4 className="text-sm font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                   <FileCheck size={16} /> Documents to Upload
                 </h4>
                 <div className="space-y-3">
                   {/* Document 1: Liability Transfer Pack (Required) */}
                   <div>
                     <div className="flex items-center justify-between mb-1">
                       <div className="flex items-center gap-2">
                         <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">1</div>
                         <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">Liability Transfer Pack</span>
                         <span className="text-xs font-bold px-2 py-0.5 bg-blue-600 text-white rounded">Required</span>
                       </div>
                       <a
                         href={packUrl || '#'}
                         target="_blank"
                         rel="noreferrer"
                         className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700 transition-colors"
                       >
                         <Download size={12} /> Download
                       </a>
                     </div>
                     <p className="text-xs text-blue-700 dark:text-blue-300 pl-8">
                       Complete PDF with cover letter + hire agreement. Upload this to the council portal.
                     </p>
                   </div>

                   {/* Document 2: Hire Agreement Only (Backup) */}
                   <div>
                     <div className="flex items-center justify-between mb-1">
                       <div className="flex items-center gap-2">
                         <div className="w-6 h-6 rounded-full bg-slate-400 text-white text-xs font-bold flex items-center justify-center">2</div>
                         <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Hire Agreement Only</span>
                         <span className="text-xs font-bold px-2 py-0.5 bg-slate-400 text-white rounded">Backup</span>
                       </div>
                       <button
                         type="button"
                         onClick={() => void openPrivateDocument(linkedHire?.agreement_url)}
                         disabled={!linkedHire?.agreement_url}
                         className="inline-flex items-center gap-1 px-3 py-1 bg-slate-600 text-white rounded text-xs font-bold hover:bg-slate-700 transition-colors"
                       >
                         <Download size={12} /> Download
                       </button>
                     </div>
                     <p className="text-xs text-slate-600 dark:text-slate-400 pl-8">
                       Standalone hire agreement. Only needed if council requests it separately.
                     </p>
                   </div>
                 </div>
               </div>

               <div className="flex justify-end gap-2">
                 <button 
                   onClick={() => setTransferStep('pack')}
                   className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium"
                 >
                   Back
                 </button>
                 <button 
                   onClick={() => setTransferStep('proof')}
                   className="px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 flex items-center gap-2"
                 >
                   <CheckCircle2 size={16} /> I have submitted
                 </button>
               </div>
            </div>
          ) : transferStep === 'proof' ? (
            /* SUBMISSION PROOF STEP */
            <div className="space-y-6 animate-in slide-in-from-right-4">
               <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg flex gap-3 border border-blue-100 dark:border-blue-900">
                 <ShieldCheck className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-0.5" />
                 <div>
                   <h4 className="font-bold text-blue-900 dark:text-blue-100">Proof of Submission Required</h4>
                   <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                     You must upload a screenshot or receipt from the council portal to track and confirm your submission. This is mandatory for record-keeping.
                   </p>
                 </div>
               </div>

               <div className="p-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-900/50 flex flex-col items-center justify-center text-center">
                  {!submissionProofFile ? (
                    <>
                      <Upload className="w-10 h-10 text-slate-400 mb-3" />
                      <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Upload Receipt / Screenshot (Required)</p>
                      <p className="text-xs text-slate-700 dark:text-slate-300 mb-4">PNG, JPG, or PDF • Max 10MB</p>
                      <FileUploadRow
                        label="Select File"
                        isComplete={false}
                        onUpload={setSubmissionProofFile}
                        uploading={false}
                        icon={<FileText size={16} />}
                      />
                    </>
                  ) : (
                    <div className="w-full flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                       <div className="flex items-center gap-3">
                         <FileText className="text-primary-600" size={20} />
                         <span className="text-sm font-medium text-slate-800 dark:text-white">{submissionProofFile.name}</span>
                       </div>
                       <button onClick={() => setSubmissionProofFile(null)} className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-1 rounded">
                         <XCircle size={18} />
                       </button>
                    </div>
                  )}
               </div>

               {/* Reference Number Input */}
               <div className="space-y-2">
                 <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                   Reference Number (Optional)
                 </label>
                 <input
                   type="text"
                   value={transferReferenceNumber}
                   onChange={(e) => setTransferReferenceNumber(e.target.value)}
                   placeholder="e.g., REF-12345678"
                   className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                 />
                 <p className="text-xs text-slate-700 dark:text-slate-300">
                   Enter the reference number provided by the council portal (if any)
                 </p>
               </div>

               <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 p-2 rounded">
                 <Clock size={12} />
                 <span>Recording submission to {detectedTransferAuthority?.name || ticket.issuing_authority}</span>
               </div>

               <div className="flex justify-end gap-2 pt-2">
                 <button 
                   onClick={() => setTransferStep('helper')}
                   className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium"
                 >
                   Back
                 </button>
                 <button 
                   onClick={handleSubmitTransferProof}
                   disabled={!submissionProofFile || uploadingDoc}
                   className="px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                 >
                   {uploadingDoc ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                   Complete Transfer
                 </button>
               </div>
            </div>
          ) : (
            /* CHECK & PACK GENERATION STEPS */
            <>
              {/* Authority Detection */}
              {detectedTransferAuthority && (
                <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-bold text-blue-900 dark:text-blue-100 text-sm">Authority Detected</h4>
                      <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                        {detectedTransferAuthority.name}
                      </p>
                      <div className="mt-2 flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
                        <span className={`px-2 py-0.5 rounded ${
                          detectedTransferAuthority.confidence === 'verified'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                        }`}>
                          {detectedTransferAuthority.confidence}
                        </span>
                        {detectedTransferAuthority.portal_url && (
                          <span className="text-blue-600 dark:text-blue-400">• Portal available</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Checklist Visual */}
              <div className="space-y-4 mb-6">
                <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Eligibility Check</h4>

                <CheckItem
                  label="Ticket Type Supports Transfer"
                  status="success"
                  detail={packType === 'TMA_2004' ? 'Council PCN (TMA 2004)' : 'Private Parking Charge (PoFA)'}
                />

                <CheckItem
                  label="Driver Assigned & Hired"
                  status={ticket.assigned_driver_name && linkedHire ? 'success' : 'error'}
                  detail={linkedHire ? `Ref: ${linkedHire.hire_ref}` : 'No active hire record found'}
                />

                <CheckItem
                  label="Hire Agreement Document"
                  status={linkedHire?.agreement_url ? 'success' : 'error'}
                  detail={linkedHire?.agreement_url ? 'Document uploaded' : 'Please upload the hire agreement'}
                />

                <CheckItem
                  label="Liability Clause Present"
                  status={liabilityConfirmed ? 'success' : 'error'}
                  detail="Agreement must contain specific liability acceptance clause."
                />
              </div>

              {/* Action Area */}
              {!packUrl ? (
                <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-800 space-y-4">
                  
                  {/* Missing Agreement Upload */}
                  {!linkedHire?.agreement_url && (
                    <FileUploadRow 
                      label="Upload Hire Agreement"
                      subLabel="Single PDF containing hire details + liability clause"
                      isComplete={false}
                      onUpload={(file) => handleUploadDocument(file, 'agreement')}
                      uploading={uploadingDoc}
                      icon={<FileCheck size={18} />}
                    />
                  )}

                  {/* Liability Verification */}
                  {linkedHire?.agreement_url && (
                    <div className="flex items-start gap-3 p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <input 
                        type="checkbox" 
                        id="liability-check"
                        checked={liabilityConfirmed}
                        onChange={(e) => setLiabilityConfirmed(e.target.checked)}
                        className="mt-1 h-4 w-4 text-primary-600 rounded border-slate-300 focus:ring-primary-500 cursor-pointer"
                      />
                      <label htmlFor="liability-check" className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                        <strong>I confirm this agreement contains a liability clause.</strong>
                        <p className="text-xs text-slate-700 dark:text-slate-300 mt-1">
                          "The hirer acknowledges liability for all PCNs/Fines incurred during the hire period."
                        </p>
                      </label>
                    </div>
                  )}

                  {linkedHire?.agreement_url && !linkedHire?.agreement_has_liability_clause && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                      <div className="grid gap-4 md:grid-cols-[1.05fr_1fr]">
                        <div className="flex gap-3">
                          <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                              Liability clause not detected
                            </p>
                            <p className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                              Upload an updated agreement and we'll re-check the clause, then return you here.
                            </p>
                            {linkedHire && (
                              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-amber-900 dark:text-amber-200">
                                <span className="px-2 py-0.5 rounded bg-amber-100/70 dark:bg-amber-900/40 border border-amber-200/60 dark:border-amber-800/60">
                                  {linkedHire.hire_ref || 'Hire Ref —'}
                                </span>
                                <span className="px-2 py-0.5 rounded bg-amber-100/70 dark:bg-amber-900/40 border border-amber-200/60 dark:border-amber-800/60">
                                  {linkedHire.driver_name || 'Driver'}
                                </span>
                                <span className="px-2 py-0.5 rounded bg-amber-100/70 dark:bg-amber-900/40 border border-amber-200/60 dark:border-amber-800/60">
                                  {linkedHire.start_date ? new Date(linkedHire.start_date).toLocaleDateString('en-GB') : 'Start'}{linkedHire.end_date ? ` → ${new Date(linkedHire.end_date).toLocaleDateString('en-GB')}` : ''}
                                </span>
                              </div>
                            )}
                            <div className="mt-4 grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => agreementReplaceRef.current?.click()}
                                disabled={uploadingDoc || updatingAgreement}
                                className="px-3 py-2 text-xs font-bold bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-60"
                              >
                                {updatingAgreement ? 'Updating…' : 'Update Agreement'}
                              </button>
                              {linkedHire?.id && ticket?.id ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigate(`/hires?editHire=${linkedHire.id}&returnTicket=${ticket.id}&resumeTransfer=1`);
                                  }}
                                  className="px-3 py-2 text-xs font-bold text-amber-900 dark:text-amber-100 border border-amber-300 dark:border-amber-700 rounded hover:bg-amber-100/60 dark:hover:bg-amber-900/30"
                                >
                                  Open Hire Record
                                </button>
                              ) : (
                                <div className="px-3 py-2 text-xs font-bold text-amber-400 border border-amber-200/60 rounded text-center opacity-60">
                                  Open Hire Record
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="rounded-md border border-amber-200/70 dark:border-amber-800/60 bg-white/70 dark:bg-slate-900/40 p-3">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                              Required Clause
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(LIABILITY_CLAUSE_TEMPLATE);
                                addToast('Clause copied', 'info');
                              }}
                              className="text-[11px] font-bold text-amber-700 dark:text-amber-200 hover:underline"
                            >
                              Copy Clause
                            </button>
                          </div>
                          <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed">
                            {LIABILITY_CLAUSE_TEMPLATE}
                          </p>
                          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                            <span className="px-2 py-0.5 rounded bg-amber-100/70 dark:bg-amber-900/40 border border-amber-200/60 dark:border-amber-800/60 text-amber-900 dark:text-amber-100">
                              Clause included verbatim
                            </span>
                            <span className="px-2 py-0.5 rounded bg-amber-100/70 dark:bg-amber-900/40 border border-amber-200/60 dark:border-amber-800/60 text-amber-900 dark:text-amber-100">
                              Hirer signature present
                            </span>
                            <span className="px-2 py-0.5 rounded bg-amber-100/70 dark:bg-amber-900/40 border border-amber-200/60 dark:border-amber-800/60 text-amber-900 dark:text-amber-100">
                              Hire dates match
                            </span>
                          </div>
                          <p className="mt-2 text-[11px] text-amber-800 dark:text-amber-200">
                            Paste under your liability/charges section, then re-upload the agreement.
                          </p>
                        </div>
                      </div>

                      <input
                        ref={agreementReplaceRef}
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUploadDocument(file, 'agreement');
                        }}
                      />
                    </div>
                  )}

                  {agreementUpdateError && (
                    <p className="text-xs text-red-600 dark:text-red-400">
                      {agreementUpdateError}
                    </p>
                  )}

                  <div className="flex justify-end pt-2">
                    <button 
                      onClick={handleGenerateLiabilityPack}
                      disabled={generatingPack || !linkedHire?.agreement_url || !liabilityConfirmed}
                      className="px-6 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
                    >
                      {generatingPack ? <Loader2 size={16} className="animate-spin" /> : <Briefcase size={16} />}
                      Generate Transfer Pack
                    </button>
                  </div>
                </div>
              ) : (
                /* Success State (Pack Ready) */
                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg border border-green-200 dark:border-green-800 text-center animate-in fade-in zoom-in-95">
                   <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                   <h3 className="font-bold text-slate-800 dark:text-white mb-1">Transfer Pack Ready</h3>
                   <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                     Generated cover letter & attached verified hire agreement.
                   </p>
                   <div className="flex flex-wrap gap-3 justify-center">
                     <a 
                       href={packUrl} 
                       target="_blank" 
                       rel="noreferrer" 
                       className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-white rounded-lg text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                     >
                       <Download size={16} /> Download Pack
                     </a>
                     <button
                       onClick={() => setTransferStep('helper')}
                       className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-colors"
                     >
                       Continue to Submission Helper <ArrowRight size={16} />
                     </button>
                     <button 
                       onClick={() => {
                          const link = CouncilPaymentService.getPortalLink(ticket.issuing_authority, ticket.pcn_number, ticket.vrn);
                          window.open(link, '_blank', 'noopener,noreferrer');
                          setTransferStep('helper');
                       }}
                       className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
                     >
                       Open Authority Portal <ExternalLink size={16} />
                     </button>
                   </div>
                </div>
              )}

              {/* Progress Overlay */}
              {generatingPack && (
                <div className="mt-4 space-y-2">
                  {generationSteps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 animate-in fade-in slide-in-from-left-2">
                       {i === generationSteps.length - 1 ? <Loader2 size={14} className="animate-spin text-primary-600" /> : <CheckCircle2 size={14} className="text-green-500" />}
                       {step}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => { if (!isDeleting) setIsDeleteModalOpen(false); }}
        title="Delete Ticket"
        maxWidth="max-w-md"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-100 dark:border-amber-900">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">This action cannot be undone.</p>
          </div>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            Are you sure you want to delete ticket <span className="font-bold text-slate-900 dark:text-white">{ticket.pcn_number}</span>?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
              className="px-4 py-2 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors disabled:opacity-70"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-70"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 size={16} />}
              Delete Ticket
            </button>
          </div>
        </div>
      </Modal>

      {/* Assignment Modal (Reused Logic) */}
      <Modal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        title="Assign Ticket to Driver"
        maxWidth="max-w-2xl"
      >
        {/* ... existing modal content ... */}
        <div className="p-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900 rounded-lg p-4 mb-6 grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Vehicle</p>
              <div className="flex items-center gap-2">
                <Car size={16} className="text-blue-500" />
                <span className="font-bold text-slate-800 dark:text-white">{ticket.vrn}</span>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Contravention Date</p>
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-blue-500" />
                <span className="font-medium text-slate-800 dark:text-white">{new Date(ticket.contravention_date || ticket.issue_date).toLocaleDateString('en-GB')}</span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-3 flex items-center gap-2">
              <UserCheck size={16} className="text-primary-600" />
              Suggested Matches
            </h3>
            
            {loadingHires ? (
              <div className="py-4 flex justify-center">
                <Loader2 className="animate-spin text-slate-400" />
              </div>
            ) : suggestedHires.length > 0 ? (
              <div className="space-y-3">
                {suggestedHires.map(hire => (
                  <div key={hire.id} className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg p-4 flex items-center justify-between hover:shadow-sm transition-shadow">
                    <div>
                      <p className="font-bold text-slate-800 dark:text-white">{hire.driver_name}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                            (hire.match_score ?? 0) >= 90
                              ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800'
                              : (hire.match_score ?? 0) >= 70
                                ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800'
                                : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
                          }`}
                          title="Match score"
                        >
                          Score: {hire.match_score ?? 0}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded border bg-white/70 dark:bg-slate-950/30 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                          {hire.match_label || 'Vehicle match'}
                        </span>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                            hire.compliance_status === 'Compliant'
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800'
                              : hire.compliance_status === 'At Risk'
                                ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
                                : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
                          }`}
                          title="Agreement compliance"
                        >
                          Agreement: {hire.compliance_status || 'Unknown'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400 mt-1">
                        {hire.hire_ref && <span>Ref: {hire.hire_ref}</span>}
                        <span>{new Date(hire.start_date).toLocaleDateString('en-GB')} - {hire.end_date ? new Date(hire.end_date).toLocaleDateString('en-GB') : 'Ongoing'}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAssignHire(hire)}
                      disabled={assigning}
                      className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors flex items-center gap-1"
                    >
                      {assigning ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                      Assign
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-slate-800 border-dashed">
                <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">No automatic matches found.</p>
                <p className="text-xs text-slate-600 dark:text-slate-300 mb-4">
                  If there was no hire at the time of this PCN, confirm below to proceed.
                </p>
                {!confirmingNoHire ? (
                  <button
                    onClick={() => setConfirmingNoHire(true)}
                    className="px-4 py-2 bg-amber-100 text-amber-700 text-xs font-bold rounded border border-amber-200 hover:bg-amber-200 transition-colors dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800 dark:hover:bg-amber-900/50"
                  >
                    Confirm No Hire Exists
                  </button>
                ) : (
                  <div className="text-left p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200 mb-2">Select reason:</p>
                    <div className="grid gap-2 mb-3">
                      {[
                        { value: 'hire_not_yet_uploaded', label: 'Hire record not yet uploaded' },
                        { value: 'vehicle_company_owned', label: 'Vehicle was company-owned at the time' },
                        { value: 'third_party_error', label: 'Third-party error' },
                        { value: 'other', label: 'Other' },
                      ].map((option) => {
                        const isActive = noHireReason === option.value;
                        return (
                          <SelectableOptionButton
                            key={option.value}
                            onClick={() => setNoHireReason(option.value as any)}
                            selected={isActive}
                            tone="primary"
                            compact
                          >
                            {option.label}
                          </SelectableOptionButton>
                        );
                      })}
                    </div>
                    <input
                      type="text"
                      placeholder="Additional notes (optional)"
                      value={noHireNote}
                      onChange={(e) => setNoHireNote(e.target.value)}
                      className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white mb-3"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => setConfirmingNoHire(false)}
                        className="flex-1 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmNoHire}
                        disabled={!noHireReason || confirmingNoHireLoading}
                        className="flex-1 px-3 py-2 text-xs font-bold bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                      >
                        {confirmingNoHireLoading ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        Confirm
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 dark:border-slate-800 my-4"></div>

          <div>
            <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-3">Search Other Hires</h3>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by driver name..."
                value={hireSearchTerm}
                onChange={(e) => handleSearchHires(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-800 dark:text-white"
              />
            </div>

            {otherHires.length > 0 && (
              <div className="space-y-2 mb-4">
                {otherHires.map(hire => (
                  <div key={hire.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800">
                    <div>
                      <p className="font-medium text-slate-800 dark:text-white text-sm">{hire.driver_name}</p>
                      <p className="text-xs text-slate-700 dark:text-slate-300">{new Date(hire.start_date).toLocaleDateString('en-GB')} - {hire.end_date ? new Date(hire.end_date).toLocaleDateString('en-GB') : 'Ongoing'}</p>
                    </div>
                    <button
                      onClick={() => handleAssignHire(hire)}
                      disabled={assigning}
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-800 text-xs font-medium"
                    >
                      Select
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded-lg flex items-center justify-between">
              <span className="text-xs text-slate-700 dark:text-slate-300">Driver not found?</span>
              <button 
                onClick={() => {
                  setIsAssignModalOpen(false);
                  navigate('/hires');
                }}
                className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 flex items-center gap-1"
              >
                Create new hire <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDriverNoticeModalOpen}
        onClose={() => {
          if (!sendingDriverNotice) setIsDriverNoticeModalOpen(false);
        }}
        title="Send to Driver"
        maxWidth="max-w-xl"
      >
        {ticket && driverNoticeRecipient && (
          <div className="p-6 space-y-5">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm dark:border-slate-700 dark:bg-slate-800/50">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Recipient</p>
                  <p className="mt-1 font-medium text-slate-900 dark:text-white">{driverNoticeRecipient.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{driverNoticeRecipient.email}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Notice type</p>
                  <p className="mt-1 font-medium text-slate-900 dark:text-white">
                    {driverNoticeType === 'fleet_evidence_request' ? 'Evidence / context request' : 'PCN accountability notice'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">PCN</p>
                  <p className="mt-1 font-medium text-slate-900 dark:text-white">{ticket.pcn_number}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Vehicle</p>
                  <p className="mt-1 font-medium text-slate-900 dark:text-white">{ticket.vrn}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Authority</p>
                  <p className="mt-1 font-medium text-slate-900 dark:text-white">{ticket.issuing_authority || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Amount</p>
                  <p className="mt-1 font-medium text-slate-900 dark:text-white">£{Number(ticket.amount || 0).toFixed(2)}</p>
                </div>
              </div>
              {ticket.location && (
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">Location: {ticket.location}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Message note
              </label>
              <textarea
                value={driverNoticeNote}
                onChange={(event) => setDriverNoticeNote(event.target.value)}
                rows={4}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-primary-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                This sends a notification only. It will not create an invoice or change the ticket status.
              </p>
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
              <button
                type="button"
                onClick={() => setIsDriverNoticeModalOpen(false)}
                disabled={sendingDriverNotice}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-60 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendDriverNotice}
                disabled={sendingDriverNotice}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {sendingDriverNotice ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Send Notice
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* TE Form Wizard Modal */}
      <Modal
        isOpen={isTeFormWizardOpen}
        onClose={() => setIsTeFormWizardOpen(false)}
        title=""
        maxWidth="max-w-4xl"
        hideHeader
      >
        <div className="h-[85vh] flex flex-col">
          <TeFormWizard
            formType="te9"
            includeOutOfTime={tecForms?.isOutOfTime ?? false}
              ticket={{
                pcn_number: ticket?.pcn_number,
                vrn: ticket?.vrn,
                location: ticket?.location,
                issue_date: ticket?.issue_date,
                contravention_date: ticket?.contravention_date,
                issuing_authority: ticket?.issuing_authority,
                assigned_driver_name: ticket?.assigned_driver_name,
              }}
            organization={{
              name: organization?.name,
              address_line1: (organization as (typeof organization & { address_line1?: string }) | null)?.address_line1,
              address_line2: (organization as (typeof organization & { address_line2?: string }) | null)?.address_line2,
              city: (organization as (typeof organization & { city?: string }) | null)?.city,
              postcode: (organization as (typeof organization & { postcode?: string }) | null)?.postcode,
            }}
            driver={linkedDriver ? {
              full_name: (linkedDriver as Driver & { full_name?: string }).full_name || linkedDriver.name,
              address_line1: linkedDriver.address_line1,
              address_line2: (linkedDriver as Driver & { address_line2?: string }).address_line2,
              city: linkedDriver.city,
              postcode: linkedDriver.postcode,
            } : undefined}
            onCancel={() => setIsTeFormWizardOpen(false)}
            onSubmit={async (formData, pdfBytes) => {
              setTeFormPdfBytes(pdfBytes);
              setTecSignatureDataUrl(formData.signatureDataUrl || null);
              setIsTeFormWizardOpen(false);

              // Prepare email attachments from generated PDFs
              const attachments: Array<{ filename: string; content: string; type: string }> = [];

              if (pdfBytes.te9) {
                attachments.push({
                  filename: `TE9_${formData.vehicleRegistration || ticket?.vrn || 'form'}.pdf`,
                  content: toBase64(pdfBytes.te9),
                  type: 'application/pdf',
                });
              }

              if (pdfBytes.te7) {
                attachments.push({
                  filename: `TE7_${formData.vehicleRegistration || ticket?.vrn || 'form'}.pdf`,
                  content: toBase64(pdfBytes.te7),
                  type: 'application/pdf',
                });
              }

              // Open the TEC submission modal with pre-filled data
              setTecAttachmentFiles([]);
              setIsTecSubmitOpen(true);

              // Auto-send or let user review
              if (attachments.length > 0 && !isMockMode) {
                const formsLabel = attachments.map(a => a.filename.split('_')[0]).join(' + ');
                const subject = `TEC Submission: ${formData.vehicleRegistration} - ${formData.penaltyChargeNumber}`;

                try {
                  const response = await EmailService.sendTecSubmissionEmail({
                    to: tecEmailTo.trim() || 'tec@justice.gov.uk',
                    subject,
                    data: {
                      contactName: formData.fullName,
                      contactEmail: formPrefill.email || profile.email || undefined,
                      contactPhone: formPrefill.phone,
                      pcnNumber: formData.penaltyChargeNumber,
                      vrm: formData.vehicleRegistration,
                      forms: formsLabel,
                    },
                    attachments,
                    replyTo: formPrefill.email || profile.email || undefined,
                    userId: session?.user?.id,
                    organisationId: organization?.id,
                  });

                  if (response.success) {
                    addToast(`TE forms sent successfully to ${tecEmailTo || 'tec@justice.gov.uk'}`, 'success');
                    setIsTecSubmitOpen(false);

                    // Update ticket timeline
                    if (ticket) {
                      const event: TimelineEvent = {
                        id: `te-form-${Date.now()}`,
                        date: new Date().toISOString(),
                        title: 'TE forms submitted',
                        description: `${formsLabel} sent to TEC via email.`,
                        type: 'email',
                      };
                      const timeline: TimelineEvent[] = [...(ticket.timeline || []), event];
                      await ApiService.updateTicket(ticket.id, { timeline });
                      setTicket({ ...ticket, timeline });
                    }
                  } else {
                    addToast(`Failed to send: ${response.error}`, 'error');
                  }
                } catch (err) {
                  console.error('Failed to send TE forms:', err);
                  addToast(`Error sending forms: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
                }
              }
            }}
          />
        </div>
      </Modal>

      {/* New Investigation Modal */}
      <InvestigationModal
        isOpen={isInvestigationModalOpen}
        onClose={() => setIsInvestigationModalOpen(false)}
        ticket={ticket}
        vehicle={vehicle}
        onComplete={handleInvestigationComplete}
      />
      {/* Submission Wizard */}
      {isSubmissionWizardOpen && ticket && (
        <SubmissionWizard
          ticket={ticket}
          submissionType={submissionType}
          appealText={submissionType === 'appeal' ? appealText : undefined}
          onComplete={handleSubmissionComplete}
          onCancel={() => setIsSubmissionWizardOpen(false)}
        />
      )}

      {/* Create Invoice Modal */}
      <CreateInvoiceModal
        isOpen={isCreateInvoiceModalOpen}
        onClose={() => setIsCreateInvoiceModalOpen(false)}
        onSuccess={async () => {
          setIsCreateInvoiceModalOpen(false);
          // Refresh ticket to get updated invoice status
          if (id) {
            try {
              const data = await ApiService.getTicket(id);
              if (data) setTicket(data);
            } catch (err) {
              console.error('Error refreshing ticket:', err);
            }
          }
        }}
        preselectedDriverId={recoveryDecision?.resolvedDriverId || linkedDriver?.id}
        preselectedTicketId={ticket?.id}
      />

      {/* Appeal Preparation Checklist Modal - Phase 1.4 */}
      {ticket && (
        <AppealChecklistModal
            isOpen={isAppealChecklistOpen}
            onClose={() => setIsAppealChecklistOpen(false)}
            onConfirmSubmit={handleConfirmAppealSubmission}
            ticketInfo={{
              pcnNumber: ticket.pcn_number || 'N/A',
            appealDeadline: appealDeadlineDate,
              hasEvidence: evidenceItems.length > 0
            }}
          />
      )}
    </div>
  );
};

export default TicketDetail;
